import { useEffect, useRef, useState, useCallback, useMemo } from 'react'

import { positionsService } from '@/services/positions'
import { botsService } from '@/services/bots'
import { tradingSignalsService } from '@/services/tradingSignals'
import { chartingService } from '@/services/charting'
import { exchangeAccountsService } from '@/services/exchangeAccounts'
import usePositionStore from '@/store/positionStore'
import useUiStore from '@/store/uiStore'
import { useOutsideClick } from '@/hooks/useOutsideClick'
import LoadingSpinner from '@/components/Common/LoadingSpinner'
import { useIndicatorConfig, buildCellKey } from '@/hooks/useIndicatorConfig'
import { computeRangoBotDivergence } from '@/indicators/rango_quantum/botDivergence'
import { resolveConfig as resolveRangoConfig } from '@/indicators/rango_quantum/constants'
import { useChartLayout } from '@/hooks/useChartLayout'
import { useIndicatorState } from '@/hooks/useIndicatorState'
import { useChartPanTick } from '@/hooks/useChartPanTick'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { initRemotePreferences, setRemotePatchFn, getRemoteValue, saveRemote } from '@/hooks/remotePreferences'
import { useEmaSmaSync } from '@/hooks/useEmaSmaSync'
import { useEmaSmaNotifications, useEmaSmaWatchToggleToast } from '@/hooks/useEmaSmaNotifications'
import EmaSmaNotificationToast from '@/indicators/ema_sma_quantum/NotificationToast.jsx'
import { fetchMtfIndicatorData, sanitizeHtfTimeframes } from '@/utils/mtf'
import { ensureIndicatorRange } from '@/utils/indicatorRange'
import { deriveCandleSource, resolveSelectionSource } from '@/utils/symbolSource'
import api from '@/services/api'
import { indicators, getIndicator } from '@/indicators/registry'
import { createChartDrawingApi } from '@/core/createChartDrawingApi'
import IndicatorToggle from '@/components/Chart/IndicatorToggle'
import IndicatorOverlayLabels from '@/components/Chart/IndicatorOverlayLabels'
import TimeframeSelector from '@/components/Chart/TimeframeSelector'
import CandleCountdown from '@/components/Chart/CandleCountdown'
import FavoritesMenu from '@/components/Chart/FavoritesMenu'
import { TIMEFRAMES } from '@/constants/timeframes'
import {
  TrendingUp, TrendingDown, AlertTriangle, Activity,
  BarChart3, ChevronDown, Eye, EyeOff, LineChart, X,
  Maximize2, Minimize2, CandlestickChart, AreaChart, Layers,
  Crosshair, Loader2, Plus, Minus, Pencil,
} from 'lucide-react'
import { manualTradeService } from '@/services/manualTrade'
import DrawingTools from '@/components/Chart/DrawingTools'
import ICTCanvas from '@/components/Chart/ICTCanvas'
import ICTConfigPanel, { DEFAULT_ICT_CONFIG } from '@/components/Chart/ICTConfigPanel'
import ICTBadge from '@/components/Chart/ICTBadge'
import { setExternalData as setChochBosQuantumData } from '@/indicators/choch_bos_quantum'
import { setExternalData as setOrderBlocksQuantumData } from '@/indicators/order_blocks_quantum'
import { setMitigationBlockQuantumData } from '@/indicators/mitigation_block_quantum'
import { setLiquidityPoolsQuantumData } from '@/indicators/liquidity_pools_quantum'
import { setChartContext as setPrqChartContext } from '@/indicators/patron_ruptura_quantum/context.js'
import { setChartContext as setNasdaqQuantumChartContext } from '@/indicators/nasdaq_quantum/context.js'
import { installLwcCrashGuard } from '@/core/lwcCrashGuard'
import { useChartData } from '@/hooks/useChartData'
import { usePlaybackController } from '@/hooks/usePlaybackController'
import ChartRenderer, { BACKGROUNDS } from '@/components/Chart/ChartRenderer'
import { ChartErrorBoundary } from '@/components/Chart/ChartErrorBoundary'
import SymbolSelector from '@/components/Chart/SymbolSelector'
import PlaybackControls from '@/components/Chart/PlaybackControls'
import { error as debugError } from '@/utils/logger'

// ─── Paletas de color ─────────────────────────────────────────────────────────
const PALETTES = {
  classic: { label: 'Classic',   up: '#22c55e', down: '#ef4444' },
  tv:      { label: 'TradingView', up: '#26a69a', down: '#ef5350' },
  blue:    { label: 'Blue/Red',  up: '#3b82f6', down: '#ef4444' },
  mono:    { label: 'Mono',      up: '#94a3b8', down: '#475569' },
  warm:    { label: 'Gold/Violet', up: '#f59e0b', down: '#7c3aed' },
}

// ─── Fondos de gráfico ────────────────────────────────────────────────────────
// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatPrice(price) {
  if (price === null || price === undefined) return '—'
  const num = Number(price)
  if (num >= 1000) return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return num.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
}

function formatTime(dateString) {
  if (!dateString) return '—'
  const date = new Date(dateString)
  const now = new Date()
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleString('es-ES', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// useChartData normaliza el tiempo de velas diarias (1d/1w/1M) a string
// 'YYYY-MM-DD' para lightweight-charts. Los endpoints de backend esperan
// timestamps en segundos, así que convertimos de vuelta cuando usamos velas
// como rango de parámetros.
function candleTimeToSeconds(t) {
  if (typeof t === 'string') {
    return Math.floor(new Date(`${t}T00:00:00Z`).getTime() / 1000)
  }
  return t
}

function calculatePositionRoi(pos, currentPrice) {
  const entry = parseFloat(pos.entry_price)
  const qty   = parseFloat(pos.quantity)
  const lev   = parseFloat(pos.leverage || 1)
  let pnl = parseFloat(pos.unrealized_pnl || 0)
  if (currentPrice && entry > 0 && qty > 0) {
    pnl = pos.side === 'long' ? (currentPrice - entry) * qty : (entry - currentPrice) * qty
  }
  const margin = entry > 0 && qty > 0 ? (entry * qty) / lev : 0
  return margin > 0 ? (pnl / margin) * 100 : 0
}

const normalizeSymbol = (s) =>
  s?.replace(':USDT','').replace(':USDC','').replace('/USDT','').replace('/USDC','').toUpperCase()

function toCcxtSymbol(raw) {
  if (!raw) return raw
  if (raw.includes('/')) return raw
  let s = raw.replace(/\.P$/i, '').replace(/_/g, '-')
  if (s.includes('-')) {
    const [base, quote] = s.split('-')
    return `${base}/${quote}:${quote}`
  }
  const m = s.match(/^(.*?)(USDT|USDC|BTC|ETH|USD)$/i)
  if (m) return `${m[1]}/${m[2]}:${m[2]}`
  return raw
}

// ─── Position overlay helpers ─────────────────────────────────────────────────
function snapToCandle(ts, data) {
  if (!data || data.length === 0) return ts
  const exact = data.find(c => c.time === ts)
  if (exact) return exact.time
  let idx = data.length - 1
  while (idx >= 0 && data[idx].time > ts) idx--
  if (idx >= 0) return data[idx].time
  return data[0].time
}

function clampToRange(ts, data) {
  if (!data || data.length === 0) return ts
  if (ts < data[0].time) return data[0].time
  if (ts > data[data.length - 1].time) return data[data.length - 1].time
  return ts
}

function buildPositionMarkers(pos, data, signals, symbolNorm) {
  const rawEntry = Math.floor(new Date(pos.opened_at).getTime() / 1000)
  const rawClose = pos.closed_at ? Math.floor(new Date(pos.closed_at).getTime() / 1000) : null
  const entryTime = snapToCandle(rawEntry, data)
  const closeTime = rawClose ? snapToCandle(rawClose, data) : null
  const entryPrice = parseFloat(pos.entry_price)
  const isLong = pos.side === 'long'
  const posColor = isLong ? '#22c55e' : '#ef4444'

  const markers = []
  const posOpenedMs = new Date(pos.opened_at).getTime()

  let relatedSignal = signals.find(s => s.position_id === pos.id)
  if (!relatedSignal) {
    relatedSignal = signals
      .filter(s => normalizeSymbol(toCcxtSymbol(s.symbol)) === symbolNorm && s.action === pos.side)
      .map(s => ({ ...s, _delta: Math.abs(new Date(s.received_at).getTime() - posOpenedMs) }))
      .filter(s => s._delta < 2 * 60 * 1000)
      .sort((a, b) => a._delta - b._delta)[0]
  }

  if (relatedSignal) {
    const signalTime = snapToCandle(Math.floor(new Date(relatedSignal.received_at).getTime() / 1000), data)
    markers.push({ time: signalTime, position: relatedSignal.action === 'long' ? 'belowBar' : 'aboveBar', color: relatedSignal.action === 'long' ? '#22c55e' : '#ef4444', shape: relatedSignal.action === 'long' ? 'arrowUp' : 'arrowDown', text: relatedSignal.action === 'long' ? 'LONG' : 'SHORT', size: 2 })
  }

  markers.push({ time: entryTime, position: isLong ? 'belowBar' : 'aboveBar', color: posColor, shape: isLong ? 'arrowUp' : 'arrowDown', text: 'Entry', size: 2 })
  if (closeTime) {
    const pnl = parseFloat(pos.realized_pnl || 0)
    markers.push({ time: closeTime, position: isLong ? 'aboveBar' : 'belowBar', color: pnl >= 0 ? '#22c55e' : '#ef4444', shape: 'circle', text: `${pnl >= 0 ? '+' : ''}${pnl.toFixed(1)}`, size: 2 })
  }

  return markers.sort((a, b) => {
    if (typeof a.time === 'number' && typeof b.time === 'number') return a.time - b.time
    return String(a.time).localeCompare(String(b.time))
  })
}

function buildDurationLineData(pos, data) {
  const rawEntry = Math.floor(new Date(pos.opened_at).getTime() / 1000)
  const rawClose = pos.closed_at ? Math.floor(new Date(pos.closed_at).getTime() / 1000) : null
  const entryTime = snapToCandle(rawEntry, data)
  const closeTime = rawClose ? snapToCandle(rawClose, data) : null
  const entryPrice = parseFloat(pos.entry_price)
  const rawEnd = closeTime || data[data.length - 1]?.time || entryTime
  const lineStart = clampToRange(entryTime, data)
  const lineEnd = clampToRange(rawEnd, data)
  if (lineEnd <= lineStart) return null
  return { lineStart, lineEnd, entryPrice, isLong: pos.side === 'long' }
}

function renderDurationLine(chart, durData) {
  if (!chart || !durData) return null
  const { lineStart, lineEnd, entryPrice, isLong } = durData
  const posColor = isLong ? '#22c55e' : '#ef4444'
  const durSeries = chart.addLineSeries({ color: posColor, lineWidth: 3, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false })
  durSeries.setData([{ time: lineStart, value: entryPrice }, { time: lineEnd, value: entryPrice }])
  const bandSeries = chart.addAreaSeries({ topColor: isLong ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', bottomColor: 'rgba(0,0,0,0)', lineColor: 'rgba(0,0,0,0)', lineWidth: 0, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false })
  bandSeries.setData([{ time: lineStart, value: entryPrice }, { time: lineEnd, value: entryPrice }])
  return { durSeries, bandSeries }
}

// ─── Indicadores técnicos ─────────────────────────────────────────────────────
function sma(data, period) {
  return data.map((_, i) => {
    if (i < period - 1) return null
    const sum = data.slice(i - period + 1, i + 1).reduce((a, b) => a + b.close, 0)
    return { time: data[i].time, value: sum / period }
  })
}

function ema(data, period) {
  const k = 2 / (period + 1)
  let prev = null
  return data.map((c, i) => {
    if (i < period - 1) return null
    if (i === period - 1) prev = data.slice(0, period).reduce((a, b) => a + b.close, 0) / period
    else prev = c.close * k + prev * (1 - k)
    return { time: c.time, value: prev }
  })
}

function rsi(data, period = 14) {
  const result = []
  let avgGain = 0, avgLoss = 0
  for (let i = 1; i <= period; i++) {
    const c = data[i].close - data[i - 1].close
    if (c > 0) avgGain += c; else avgLoss -= c
  }
  avgGain /= period; avgLoss /= period
  for (let i = 0; i < data.length; i++) {
    if (i < period) { result.push(null); continue }
    const c = data[i].close - data[i - 1].close
    avgGain = (avgGain * (period - 1) + Math.max(c, 0)) / period
    avgLoss = (avgLoss * (period - 1) + Math.max(-c, 0)) / period
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss
    result.push({ time: data[i].time, value: 100 - (100 / (1 + rs)) })
  }
  return result
}

function calculateIndicators(candles) {
  if (!candles || candles.length < 200) return null
  return {
    ema20:  ema(candles, 20),
    ema50:  ema(candles, 50),
    sma200: sma(candles, 200),
    rsi14:  rsi(candles, 14),
    volume: candles.map(c => ({ time: c.time, value: c.volume || 0 })),
  }
}

// ─── ICT Engine importado desde frontend/src/core/ictEngine.js ─────────────
// ─── Sub-componentes ──────────────────────────────────────────────────────────
function IndicatorBadge({ name, value, color = 'blue' }) {
  const colors = {
    blue:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  }
  if (value === null || value === undefined) return null
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${colors[color]}`}>
      <span className="opacity-70">{name}:</span>
      <span>{typeof value === 'number' ? value.toFixed(2) : value}</span>
    </div>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ChartPage() {
  // Preferencias del chart ligadas a la cuenta (users.chart_preferences) en
  // vez de sólo al navegador — ver hooks/remotePreferences.js. Mismo patrón
  // que ChartPublicPage.jsx, con el cliente axios de la app privada.
  //
  // DEBE ir antes que useIndicatorState()/useChartLayout(): esos hooks
  // llaman a getRemoteValue() en su propio efecto de montaje, y
  // getRemoteValue() await-ea la promesa `loadedGate` vigente en el momento
  // exacto en que se ejecuta — si corriera antes de que este efecto llame a
  // initRemotePreferences() (que reasigna `loadedGate` a una promesa nueva),
  // se quedaría esperando para siempre la promesa vieja, huérfana de
  // resolver. Los efectos de un componente corren en el orden en que sus
  // hooks fueron declarados, así que este bloque tiene que ser el primer
  // hook del componente para garantizar que su efecto se monte primero.
  useEffect(() => {
    setRemotePatchFn((key, value) => api.patch('/auth/chart-preferences', { key, value }))
    initRemotePreferences(() => api.get('/auth/chart-preferences').then(r => r.data.preferences))
  }, [])

  // Refs
  const chartContainerRef  = useRef(null)
  const chartRef           = useRef(null)
  const candleSeriesRef    = useRef(null)
  const panTick = useChartPanTick(chartRef)
  const overlayPriceLinesRef = useRef([])
  const overlaySeriesRef     = useRef([])
  const overlayMarkersRef    = useRef([])
  const posMarkersRef        = useRef([])
  const positionPriceLinesRef = useRef([])
  const positionLineSeriesRef = useRef(null)
  const slPriceLineRef        = useRef(null)
  const tpPriceLinesRef       = useRef([])
  const draggingLineRef       = useRef(null)   // 'sl' | 'tp_0' | 'tp_1' | null
  const dragNewPriceRef       = useRef(null)
  const savedRangeRef         = useRef(null)   // { range, data } – restored on chart recreation
  const indicatorsMenuRef  = useRef(null)
  const tfMenuRef          = useRef(null)
  const paletteMenuRef     = useRef(null)
  const bgMenuRef          = useRef(null)
  const appearanceKeyRef   = useRef('')
  const overlayLabelsRawRef = useRef([])
  // Coloreado dinámico de velas por indicador (supertrend_quantum) — ver el
  // comentario en la prop `candleColorsRef` de ChartRenderer.jsx.
  const candleColorsRef    = useRef(null)
  const [colorVersion, setColorVersion] = useState(0)
  const bumpColorVersion  = useCallback(() => setColorVersion(v => v + 1), [])
  // Token del run de render del efecto de indicadores (mismo mecanismo que en
  // ChartPublicPage): distingue un dueño del coloreado que re-registró en este
  // run (aunque no-op por idéntico) de uno que ya no llama a setCandleColors.
  const colorRunRef = useRef(0)
  const lastCandleTimeRef   = useRef(null)
  const tradingModeRef     = useRef(false)
  const tradeEntryPriceRef = useRef(null)
  const tradeEntryLineRef  = useRef(null)
  // Referencia a los datos vigentes en el momento de un cambio de símbolo/
  // timeframe. useChartData emite un tick SÍNCRONO al iniciar la carga (con los
  // datos todavía del timeframe anterior) y otro cuando llegan los datos
  // reales. Guardamos la "foto" del reset para que el efecto de indicadores del
  // registry (EFFECT C) no renderice contra velas obsoletas en ese primer tick.
  const indicatorDataAtResetRef = useRef(null)
  // Entradas de EFFECT C SIN panTick: si solo cambió panTick, los indicadores
  // con renderOnPan === false no se reconstruyen (sus primitivas son nativas
  // del chart y se desplazan con el pan — el teardown/rebuild por frame era
  // el parpadeo de zonas al arrastrar).
  const lastRegistryRenderInputsRef = useRef(null)

  // State
  const [loading, setLoading]               = useState(true)
  const [availableSymbols, setAvailableSymbols] = useState([])
  const [selectedSymbol, setSelectedSymbol] = useState('BTC/USDT:USDT')
  const [dataSource, setDataSource] = useState('bingx')
  const [timeframe, setTimeframe]           = useState('1h')
  // Celda símbolo×TF activa para la config por símbolo/TF de los indicadores
  // (_bySymbolTf, diseño diseno-snapshot-chart-bot.md §1.1) — misma celda
  // que el chart público gracias a la normalización compartida (CCXT y
  // plano caen en 'BTCUSDT').
  const chartCellKey = buildCellKey(selectedSymbol, timeframe)
  const [positions, setPositions]           = useState([])
  const [signals, setSignals]               = useState([])
  const [bots, setBots]                     = useState([])

  // Indicators
  const {
    state: indState,
    setBuiltin: setIndBuiltin,
    toggleRegistry: toggleRegistryInd,
    setRegistry: setRegistryInd,
    addMovingAverage,
    removeMovingAverage,
    updateMovingAverage,
    toggleMovingAverage,
    activeCount: activeIndicatorCount,
  } = useIndicatorState()
  const {
    movingAverages,
    showRsi, showVolume, showICT,
    activeRegistry: activeIndicators,
  } = indState
  const setShowRsi = (v) => setIndBuiltin('showRsi', v)
  const setShowVolume = (v) => setIndBuiltin('showVolume', v)
  const setShowICT = (v) => setIndBuiltin('showICT', v)
  const [ictData, setIctData]               = useState(null)
  const [ictSignal, setIctSignal]           = useState(null)
  const [ictHistoryData, setIctHistoryData] = useState(null)
  const [ictConfig, setIctConfig]           = useState(DEFAULT_ICT_CONFIG)
  const debouncedIctConfig = useDebouncedValue(ictConfig, 400)
  const [showICTConfig, setShowICTConfig]   = useState(false)

  // Request tokens to discard out-of-order responses (e.g. a slow request for
  // the previous timeframe resolving after a faster one for the new
  // timeframe) — without this, indicator overlays could keep showing
  // data/positions from the timeframe the user just left.
  const ictReqIdRef = useRef(0)
  const ictHistoryReqIdRef = useRef(0)

  // Loading flags for the config panels' spinner — the ICT/SMC calculation in
  // particular can take several seconds (see effects below), and without any
  // feedback it looks like the indicator is broken rather than working.
  const [ictMainLoading, setIctMainLoading] = useState(false)
  const [ictHistoryLoading, setIctHistoryLoading] = useState(false)

  // Drop all indicator overlay data the instant symbol/timeframe changes so
  // stale boxes/lines never linger on screen while the fresh data loads.
  useEffect(() => {
    setIctData(null)
    setIctSignal(null)
    setIctHistoryData(null)
    setChochBosQuantumData(null)
    setOrderBlocksQuantumData(null)
    setMitigationBlockQuantumData(null)
  }, [selectedSymbol, timeframe])

  // Capturar la "foto" de los datos justo antes de que useChartData inicie la
  // carga de un nuevo símbolo/timeframe. EFFECT C usa esta referencia para
  // descartar el primer tick síncrono (datos viejos) y sólo renderizar cuando
  // llegan las velas reales del nuevo timeframe.
  useEffect(() => {
    indicatorDataAtResetRef.current = dataRef?.current ?? candleData ?? null
  }, [selectedSymbol, timeframe])

  // Patrón Ruptura Quantum: contexto de símbolo/timeframe para el indicador.
  useEffect(() => {
    setPrqChartContext({ symbol: selectedSymbol, timeframe })
  }, [selectedSymbol, timeframe])

  // NASDAQ Quantum: contexto de símbolo/timeframe para el indicador.
  useEffect(() => {
    setNasdaqQuantumChartContext({ symbol: selectedSymbol, timeframe })
  }, [selectedSymbol, timeframe])

  // Contención del bug upstream LWC #2044 (compactación de escala + crosshair
  // → "Value is null"). Captura quirúrgica con métrica; ver core/lwcCrashGuard.
  useEffect(() => {
    installLwcCrashGuard(() => chartRef.current)
  }, [])

  // Indicators system
  const { configs, getConfig, setConfig } = useIndicatorConfig()
  // WP H-G: divergencia entre la config pintada de rango_quantum y la del bot
  // vivo del usuario (mismo símbolo+TF) para el badge «esta config no es la de
  // tu bot». Reusa la lista de bots ya cargada en el arranque — sin endpoint
  // nuevo. null si no hay bot vivo rango en esta celda o la config coincide.
  const rangoBotDivergence = useMemo(
    () => computeRangoBotDivergence(
      resolveRangoConfig(getConfig('rango_quantum', chartCellKey)),
      bots,
      selectedSymbol,
      timeframe,
    ),
    [configs, bots, selectedSymbol, timeframe, chartCellKey],
  )
  const { toast: emaSmaToast, dismissToast: dismissEmaSmaToast, computeNotificationUpdate: computeEmaSmaNotificationUpdate } = useEmaSmaNotifications()
  const { toggleToast: emaSmaToggleToast, dismissToggleToast: dismissEmaSmaToggleToast } = useEmaSmaWatchToggleToast(getConfig('ema_sma_quantum'))
  const chochBosConfig = getConfig('choch_bos_quantum')
  const debouncedChochBosConfig = useDebouncedValue(chochBosConfig, 400)
  const chochBosReqIdRef = useRef(0)
  const [chochBosDataVersion, setChochBosDataVersion] = useState(0)
  const orderBlocksConfig = getConfig('order_blocks_quantum')
  const debouncedOrderBlocksConfig = useDebouncedValue(orderBlocksConfig, 400)
  const orderBlocksReqIdRef = useRef(0)
  const [orderBlocksDataVersion, setOrderBlocksDataVersion] = useState(0)
  // MB Quantum: nombres propios para no colisionar con el mitigation_block
  // legacy de useStructureConfig (retirado del catálogo UI).
  const mbQuantumConfig = getConfig('mitigation_block_quantum')
  const debouncedMbQuantumConfig = useDebouncedValue(mbQuantumConfig, 400)
  const mbQuantumReqIdRef = useRef(0)
  const [mitigationBlockDataVersion, setMitigationBlockDataVersion] = useState(0)
  const [liquidityPoolsQuantumDataVersion, setLiquidityPoolsQuantumDataVersion] = useState(0)
  const [openPanels, setOpenPanels] = useState({})

  // Chart appearance (persisted via useChartLayout)
  const { layout, update: updateLayout } = useChartLayout()
  const [chartType, setChartType] = useState(layout.chartType)
  const [palette, setPalette]     = useState(layout.palette)
  const [chartBg, setChartBg]     = useState(layout.chartBg)

  useEffect(() => { updateLayout('chartType', chartType) }, [chartType])
  useEffect(() => { updateLayout('palette', palette) }, [palette])
  useEffect(() => { updateLayout('chartBg', chartBg) }, [chartBg])
  const [showBgMenu, setShowBgMenu] = useState(false)
  const [overlayLabels, setOverlayLabels] = useState([])
  const [indResults, setIndResults] = useState({})   // resultados detect() para los badges

  // UI toggles
  const [showIndicatorsMenu, setShowIndicatorsMenu] = useState(false)
  const [showTfMenu, setShowTfMenu]         = useState(false)
  const [showPaletteMenu, setShowPaletteMenu] = useState(false)
  const [selectedPosition, setSelectedPosition] = useState(null)
  const [isFullscreen, setIsFullscreen]     = useState(false)

  // Trade panel
  const [showTradePanel, setShowTradePanel]     = useState(false)
  const [tradingMode, setTradingMode]           = useState(false)
  const [tradeEntryPrice, setTradeEntryPrice]   = useState(null)
  const [tradeAccounts, setTradeAccounts]       = useState([])
  const [tradeAccountId, setTradeAccountId]     = useState('')
  const [tradeAccountType, setTradeAccountType] = useState('real')
  const [tradeLeverage, setTradeLeverage]       = useState(10)
  const [tradeSizingType, setTradeSizingType]   = useState('percentage')
  const [tradeSizingValue, setTradeSizingValue] = useState(5)
  const [tradeSlPct, setTradeSlPct]             = useState(2)
  const [tradeLoading, setTradeLoading]         = useState(false)
  const [tradeFeedback, setTradeFeedback]       = useState(null)
  const [tradeAdvOpen, setTradeAdvOpen]         = useState(false)
  const [tradeTPs, setTradeTPs]                 = useState([])
  const [tradeTrailing, setTradeTrailing]       = useState({ enabled: false, activation_profit: 1, callback_rate: 0.5 })
  const [tradeBreakeven, setTradeBreakeven]     = useState({ enabled: false, activation_profit: 1, lock_profit: 0.2 })
  const [tradeDynamicSL, setTradeDynamicSL]     = useState({ enabled: false, step_percent: 1, max_steps: 3 })
  const [favorites, setFavorites]               = useState(() => { try { return JSON.parse(localStorage.getItem('chart_favorites') || '[]') } catch { return [] } })
  const [showFavMenu, setShowFavMenu]           = useState(false)
  const favMenuRef                              = useRef(null)
  const [sidebarVisible, setSidebarVisible]     = useState({ openPos: false, closedPos: false })
  const [drawingToolbarVisible, setDrawingToolbarVisible] = useState(layout.drawingToolbarVisible)

  useEffect(() => { updateLayout('drawingToolbarVisible', drawingToolbarVisible) }, [drawingToolbarVisible])
  const [closedLoaded, setClosedLoaded]         = useState(false)
  const [strategyEdit, setStrategyEdit]         = useState({ tps: [], trailing: { enabled: false, activation_profit: 1, callback_rate: 0.5 } })
  const [strategyLoading, setStrategyLoading]   = useState(false)
  const [showStrategyPanel, setShowStrategyPanel] = useState(false)
  const [chartVersion, setChartVersion]           = useState(0)
  // Bumpeado por ChartErrorBoundary en "Reintentar": fuerza un remount limpio
  // de ChartRenderer/DrawingTools/ICTCanvas/IndicatorOverlayLabels tras un
  // error de render contenido, sin afectar al resto de la página.
  const [chartRemountKey, setChartRemountKey]     = useState(0)

  const liveController = useChartData(selectedSymbol, timeframe, dataSource)
  const pb = usePlaybackController(selectedSymbol, timeframe, liveController)
  const { data: candleData, dataRef, isLoading, error, subscribe, tick } = pb

  const prices      = usePositionStore(s => s.prices)
  const sidebarOpen = useUiStore(s => s.sidebarOpen)
  const isDark      = useUiStore(s => s.isDark)
  const chartBackground = BACKGROUNDS[chartBg]?.bg ?? (isDark ? '#111827' : '#ffffff')

  const selectedSymbolNorm = useMemo(() => normalizeSymbol(selectedSymbol), [selectedSymbol])
  const pal = PALETTES[palette] || PALETTES.classic

  // ─── Close dropdowns on outside click ───────────────────────────────────────
  useOutsideClick(indicatorsMenuRef, () => setShowIndicatorsMenu(false))
  useOutsideClick(tfMenuRef, () => setShowTfMenu(false))
  useOutsideClick(paletteMenuRef, () => setShowPaletteMenu(false))
  useOutsideClick(bgMenuRef, () => setShowBgMenu(false))
  useOutsideClick(favMenuRef, () => setShowFavMenu(false))

  // Init strategy edit panel from selected position
  useEffect(() => {
    if (!selectedPosition) { setShowStrategyPanel(false); return }
    const bot = bots.find(b => b.id === selectedPosition.bot_id)
    const tps = (selectedPosition.current_tp_prices || []).map(tp => {
      const price = parseFloat(tp?.price ?? tp)
      const entry = parseFloat(selectedPosition.entry_price)
      const profitPct = selectedPosition.side === 'long'
        ? ((price - entry) / entry * 100)
        : ((entry - price) / entry * 100)
      return { profit_percent: parseFloat(profitPct.toFixed(2)), close_percent: tp?.close_percent ?? 50 }
    })
    const trailing = bot?.trailing_config || { enabled: false, activation_profit: 1, callback_rate: 0.5 }
    setStrategyEdit({ tps, trailing: { enabled: !!trailing.enabled, activation_profit: trailing.activation_profit ?? 1, callback_rate: trailing.callback_rate ?? 0.5 } })
  }, [selectedPosition?.id])

  const toggleFavorite = (symbol) => {
    setFavorites(prev => {
      const next = prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
      localStorage.setItem('chart_favorites', JSON.stringify(next))
      saveRemote('chart_favorites', next)
      return next
    })
  }

  const favoritesRemoteAppliedRef = useRef(false)
  useEffect(() => {
    let cancelled = false
    getRemoteValue('chart_favorites').then((remote) => {
      if (cancelled || favoritesRemoteAppliedRef.current || !Array.isArray(remote)) return
      favoritesRemoteAppliedRef.current = true
      setFavorites(remote)
      try { localStorage.setItem('chart_favorites', JSON.stringify(remote)) } catch {}
    })
    return () => { cancelled = true }
  }, [])

  // ─── Load symbols ────────────────────────────────────────────────────────────
  const loadSymbols = useCallback(async (search = '') => {
    try {
      const res = await exchangeAccountsService.marketsByExchange('bingx')
      let symbols = (Array.isArray(res.data) ? res.data : []).map(s => ({
        symbol: s,
        description: s.replace('/USDT:USDT', '').replace('/USDC:USDC', ''),
      }))
      if (search) {
        const q = search.toUpperCase()
        symbols = symbols.filter(s => s.symbol.toUpperCase().includes(q))
      }
      setAvailableSymbols(symbols)
    } catch {
      setAvailableSymbols([
        { symbol: 'BTC/USDT:USDT', description: 'BTC' },
        { symbol: 'ETH/USDT:USDT', description: 'ETH' },
      ])
    }
  }, [])

  // ─── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        // Carga en paralelo: posiciones abiertas, bots, símbolos y señales al mismo tiempo
        // Las cerradas se cargan en lazy cuando el usuario abre ese panel
        const [posRes, botsRes, , sigRes] = await Promise.all([
          positionsService.unified(true),
          botsService.list(),
          loadSymbols(''),
          tradingSignalsService.list({ symbol: selectedSymbol, days: 7, limit: 100 }).catch(() => null),
        ])
        const open     = Array.isArray(posRes?.data)  ? posRes.data  : Array.isArray(posRes)  ? posRes  : []
        const botsData = Array.isArray(botsRes?.data) ? botsRes.data : Array.isArray(botsRes) ? botsRes : []
        setPositions(open)
        setBots(botsData)
        setSignals(sigRes?.data?.signals || [])
      } catch (e) { debugError('Chart load error:', e) }
      finally { setLoading(false) }
    }
    load()
  }, [])

  // ─── Merge markers helper ────────────────────────────────────────────────────
  const syncMarkers = useCallback(() => {
    if (!candleSeriesRef.current) return
    const combined = [...posMarkersRef.current, ...overlayMarkersRef.current]
      .filter(m => {
        const ok = m?.time != null
        if (!ok) debugError('[chart] marker con time inválido, se omite:', m)
        return ok
      })
      .sort((a, b) => a.time - b.time)
    candleSeriesRef.current.setMarkers(combined)
  }, [])

  // ─── Reposition HTML signal labels on scroll / zoom ──────────────────────────
  const updateLabelPositions = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current) return
    if (overlayLabelsRawRef.current.length === 0) {
      setOverlayLabels(prev => prev.length === 0 ? prev : [])
      return
    }
    const timeScale = chartRef.current.timeScale()
    const positioned = overlayLabelsRawRef.current.map(l => {
      const candle = dataRef.current.find(c => c.time === l.time)
      const price = l.price != null
        ? l.price
        : l.isBull ? (candle?.low ?? l.fallbackPrice) : (candle?.high ?? l.fallbackPrice)
      if (!Number.isFinite(price)) return null
      const y = candleSeriesRef.current.priceToCoordinate(price)
      if (y === null) return null

      let x
      if (l.barsFromLast != null) {
        // Labels flotantes "más allá" de la última vela (p.ej. niveles de un
        // trade activo): timeToCoordinate hace *snap* a la barra real más
        // cercana si el tiempo no corresponde a una vela existente, así que
        // usamos coordenadas lógicas (que sí extrapolan) y luego hacemos
        // clamp para que el label no quede oculto tras el eje de precios.
        const lastLogical = dataRef.current.length - 1
        const rawX = timeScale.logicalToCoordinate(lastLogical + l.barsFromLast)
        if (rawX === null) return null
        const paneWidth = timeScale.width()
        // +5 del "pico" triangular que precede a la caja del label.
        const estWidth = Math.max(60, (l.text?.length ?? 10) * 6.2 + 20) + 5
        x = Math.min(rawX, paneWidth - estWidth - 6)
      } else {
        x = timeScale.timeToCoordinate(l.time)
      }
      if (x === null) return null
      return { ...l, x, y }
    }).filter(Boolean)
    setOverlayLabels(positioned)
  }, [])

  // ─── EFFECT A: Chart overlays (position, cursor, labels) ───────────────────
  // ChartRenderer owns chart creation; this effect only manages overlays.
  useEffect(() => {
    const chart   = chartRef.current
    const cSeries = candleSeriesRef.current
    if (!chart || !cSeries) return

    const data = dataRef.current
    if (!data || data.length === 0) return

    // Clean previous overlays
    overlaySeriesRef.current.forEach(s => { try { chart.removeSeries(s) } catch {} })
    overlaySeriesRef.current = []
    overlayPriceLinesRef.current.forEach(l => { try { cSeries.removePriceLine(l) } catch {} })
    overlayPriceLinesRef.current = []
    positionPriceLinesRef.current.forEach(l => { try { cSeries.removePriceLine(l) } catch {} })
    positionPriceLinesRef.current = []
    if (positionLineSeriesRef.current) {
      try { chart.removeSeries(positionLineSeriesRef.current) } catch {}
      positionLineSeriesRef.current = null
    }
    if (positionLineSeriesRef._aux) {
      positionLineSeriesRef._aux.forEach(s => { try { chart.removeSeries(s) } catch {} })
      positionLineSeriesRef._aux = []
    }
    posMarkersRef.current = []
    overlayMarkersRef.current = []
    overlayLabelsRawRef.current = []

    let positionMarkers = []

    if (selectedPosition && normalizeSymbol(toCcxtSymbol(selectedPosition.symbol)) === selectedSymbolNorm) {
      const pos        = selectedPosition
      const entryPrice = parseFloat(pos.entry_price)
      const isLong     = pos.side === 'long'
      const posColor   = isLong ? '#22c55e' : '#ef4444'

      const posOpenedMs  = new Date(pos.opened_at).getTime()
      let relatedSignal  = signals.find(s => s.position_id === pos.id)
      if (!relatedSignal) {
        relatedSignal = signals
          .filter(s => normalizeSymbol(toCcxtSymbol(s.symbol)) === selectedSymbolNorm && s.action === pos.side)
          .map(s => ({ ...s, _delta: Math.abs(new Date(s.received_at).getTime() - posOpenedMs) }))
          .filter(s => s._delta < 2 * 60 * 1000)
          .sort((a, b) => a._delta - b._delta)[0]
      }

      if (relatedSignal && relatedSignal.price) {
        positionPriceLinesRef.current.push(
          cSeries.createPriceLine({ price: parseFloat(relatedSignal.price), color: isLong ? '#16a34a' : '#dc2626', lineWidth: 1, lineStyle: 3, axisLabelVisible: true, title: `Señal ${pos.side.toUpperCase()}` })
        )
      }

      positionPriceLinesRef.current.push(
        cSeries.createPriceLine({ price: entryPrice, color: posColor, lineWidth: 2, lineStyle: 0, axisLabelVisible: true, title: `Entry ${pos.side.toUpperCase()}` })
      )

      slPriceLineRef.current = null
      tpPriceLinesRef.current = []
      if (pos.current_sl_price) {
        const slLine = cSeries.createPriceLine({ price: parseFloat(pos.current_sl_price), color: '#ef4444', lineWidth: 2, lineStyle: 2, axisLabelVisible: true, title: '⬡ SL' })
        positionPriceLinesRef.current.push(slLine)
        slPriceLineRef.current = slLine
      }
      if (pos.current_tp_prices?.length > 0) pos.current_tp_prices.forEach((tp, idx) => {
        const tpLine = cSeries.createPriceLine({ price: parseFloat(tp?.price ?? tp), color: '#22c55e', lineWidth: 2, lineStyle: 2, axisLabelVisible: true, title: `⬡ TP${idx + 1}` })
        positionPriceLinesRef.current.push(tpLine)
        tpPriceLinesRef.current.push(tpLine)
      })

      const bot = bots.find(b => b.id === pos.bot_id)
      const currentSl = parseFloat(pos.current_sl_price || 0)
      if (bot?.initial_sl_percentage && currentSl > 0) {
        const initSl = isLong
          ? entryPrice * (1 - parseFloat(bot.initial_sl_percentage) / 100)
          : entryPrice * (1 + parseFloat(bot.initial_sl_percentage) / 100)
        const improved = isLong ? currentSl > initSl : currentSl < initSl
        if (improved) {
          const nearEntry = Math.abs(currentSl - entryPrice) / entryPrice < 0.005
          positionPriceLinesRef.current.push(
            cSeries.createPriceLine({ price: currentSl, color: nearEntry ? '#f59e0b' : '#a855f7', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: nearEntry ? 'BE' : 'TS' })
          )
        }
      }

      positionMarkers = buildPositionMarkers(pos, data, signals, selectedSymbolNorm)
      const durData = buildDurationLineData(pos, data)
      if (durData) {
        const { durSeries, bandSeries } = renderDurationLine(chart, durData)
        positionLineSeriesRef.current = durSeries
        if (!positionLineSeriesRef._aux) positionLineSeriesRef._aux = []
        positionLineSeriesRef._aux.push(bandSeries)
      }
    }

    positionMarkers = positionMarkers.filter(m => {
      const ok = m?.time != null
      if (!ok) debugError('[chart] position marker con time inválido, se omite:', m)
      return ok
    })
    posMarkersRef.current = positionMarkers
    cSeries.setMarkers(positionMarkers)

    // Trade entry price line
    if (tradeEntryLineRef.current) {
      try { cSeries.removePriceLine(tradeEntryLineRef.current) } catch {}
      tradeEntryLineRef.current = null
    }
    if (tradeEntryPriceRef.current) {
      try {
        tradeEntryLineRef.current = cSeries.createPriceLine({
          price: tradeEntryPriceRef.current,
          color: '#f59e0b',
          lineWidth: 2,
          lineStyle: 1,
          axisLabelVisible: true,
          title: 'Entry',
        })
      } catch {}
    }

    // Incluir líneas de SL/TP/Entry en el rango de autoescalado del precio
    // para que no se queden fuera de la vista al cambiar de timeframe/zoom.
    try {
      const linePrices = [
        ...positionPriceLinesRef.current.map(l => l.options().price),
        tradeEntryPriceRef.current,
      ].filter(p => p != null && !isNaN(p))
      if (linePrices.length > 0) {
        cSeries.applyOptions({
          autoscaleInfoProvider: (original) => {
            const res = original()
            if (!res) {
              return { priceRange: { minValue: Math.min(...linePrices), maxValue: Math.max(...linePrices) } }
            }
            const min = Math.min(res.priceRange.minValue, ...linePrices)
            const max = Math.max(res.priceRange.maxValue, ...linePrices)
            if (!Number.isFinite(min) || !Number.isFinite(max)) return res
            return { ...res, priceRange: { minValue: min, maxValue: max } }
          }
        })
      }
    } catch {}

    // Defensa en profundidad (P1, 2026-08-16, ver mismo patrón en
    // ChartPublicPage.jsx): un throw sin capturar aquí puede interrumpir a
    // mitad el bucle de paint interno de lightweight-charts — ChartErrorBoundary
    // no lo ve porque este callback lo dispara el propio LWC fuera del árbol
    // de React. try/catch como última línea de defensa, independiente de
    // cualquier guard puntual dentro de updateLabelPositions.
    let rafId = null
    const onViewChange = () => {
      if (overlayLabelsRawRef.current.length === 0) return
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        try {
          updateLabelPositions()
        } catch (e) {
          debugError('[chart] updateLabelPositions falló en onViewChange, frame descartado:', e)
        }
      })
    }
    chart.timeScale().subscribeVisibleLogicalRangeChange(onViewChange)

    // lightweight-charts v4.2.3 does not expose price-scale change events, so we
    // poll the Y coordinate of the latest close price to detect vertical pan/zoom
    // and re-snap HTML labels accordingly.
    let vertRafId = null
    let lastRefY = null
    const pollVerticalChange = () => {
      if (overlayLabelsRawRef.current.length > 0 && dataRef.current?.length) {
        const refPrice = dataRef.current[dataRef.current.length - 1].close
        const y = cSeries.priceToCoordinate(refPrice)
        if (y !== lastRefY) {
          lastRefY = y
          try {
            updateLabelPositions()
          } catch (e) {
            debugError('[chart] updateLabelPositions falló en pollVerticalChange, frame descartado:', e)
          }
        }
      }
      vertRafId = requestAnimationFrame(pollVerticalChange)
    }
    vertRafId = requestAnimationFrame(pollVerticalChange)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      if (vertRafId) cancelAnimationFrame(vertRafId)
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onViewChange)
      positionPriceLinesRef.current.forEach(l => { try { cSeries.removePriceLine(l) } catch {} })
      positionPriceLinesRef.current = []
      if (positionLineSeriesRef.current) {
        try { chart.removeSeries(positionLineSeriesRef.current) } catch {}
        positionLineSeriesRef.current = null
      }
      if (positionLineSeriesRef._aux) {
        positionLineSeriesRef._aux.forEach(s => { try { chart.removeSeries(s) } catch {} })
        positionLineSeriesRef._aux = []
      }
      if (tradeEntryLineRef.current) {
        try { cSeries.removePriceLine(tradeEntryLineRef.current) } catch {}
      }
      tradeEntryLineRef.current = null
      slPriceLineRef.current = null
      try { cSeries.applyOptions({ autoscaleInfoProvider: undefined }) } catch {}
      tpPriceLinesRef.current = []
    }
    // `tick` es necesario (igual que en el efecto de indicadores más abajo):
    // al cambiar de timeframe este efecto se dispara por el cambio de
    // `timeframe`/`selectedPosition` ANTES de que useChartData termine de
    // cargar las velas nuevas (load() llama a notify() de forma síncrona antes
    // del fetch). Sin `tick`, los markers/price-lines/línea de duración de la
    // posición se calculan una única vez contra las velas del timeframe
    // anterior y quedan “congelados” apuntando a tiempos que ya no existen en
    // la serie de velas actual — lightweight-charts lanza "Value is null" de
    // forma diferida al intentar colorear la barra en ese índice inexistente.
  }, [chartVersion, selectedPosition, signals, bots, selectedSymbolNorm, chartType, palette, chartBg, isDark, updateLabelPositions, timeframe, tick])

  // ─── EFFECT A1: Refresh position markers/duration line on new candle data ──
  // Price lines (Entry/SL/TP) are stable; only the time-dependent geometry needs
  // to be re-snapped when candles arrive after a timeframe change or live update.
  useEffect(() => {
    const chart = chartRef.current
    const cSeries = candleSeriesRef.current
    const data = dataRef.current
    const pos = selectedPosition
    if (!chart || !cSeries || !data || data.length === 0 || !pos) return
    if (normalizeSymbol(toCcxtSymbol(pos.symbol)) !== selectedSymbolNorm) return

    lastCandleTimeRef.current = null
    const update = () => {
      const currentData = dataRef.current
      if (!currentData.length) return
      const lastTime = currentData[currentData.length - 1].time
      if (lastTime === lastCandleTimeRef.current) return
      lastCandleTimeRef.current = lastTime

      const markers = buildPositionMarkers(pos, currentData, signals, selectedSymbolNorm)
      posMarkersRef.current = markers
      syncMarkers()

      const durData = buildDurationLineData(pos, currentData)
      if (!durData) {
        if (positionLineSeriesRef.current) {
          try { chart.removeSeries(positionLineSeriesRef.current) } catch {}
          positionLineSeriesRef.current = null
        }
        if (positionLineSeriesRef._aux) {
          positionLineSeriesRef._aux.forEach(s => { try { chart.removeSeries(s) } catch {} })
          positionLineSeriesRef._aux = []
        }
        return
      }

      const lineData = [
        { time: durData.lineStart, value: durData.entryPrice },
        { time: durData.lineEnd, value: durData.entryPrice },
      ]

      if (positionLineSeriesRef.current) {
        positionLineSeriesRef.current.setData(lineData)
        if (positionLineSeriesRef._aux?.[0]) {
          positionLineSeriesRef._aux[0].setData(lineData)
        }
      } else {
        const { durSeries, bandSeries } = renderDurationLine(chart, durData)
        positionLineSeriesRef.current = durSeries
        positionLineSeriesRef._aux = [bandSeries]
      }
    }

    update()
    if (!subscribe) return
    return subscribe(update)
  }, [subscribe, selectedPosition, selectedSymbolNorm, signals, syncMarkers])

  // ─── EFFECT B: Sincronización ema_sma_quantum ↔ movingAverages ─────────────
  useEmaSmaSync({ movingAverages, activeRegistry: activeIndicators, setRegistry: setRegistryInd, setConfig, addMovingAverage })

  // ─── EFFECT C: Built-in indicators (RSI / Volume) ─────────────────────────
  const rsiSeriesRef = useRef(null)
  const volumeSeriesRef = useRef(null)
  useEffect(() => {
    const chart = chartRef.current
    const cSeries = candleSeriesRef.current
    const data = dataRef.current
    if (!chart || !cSeries || !data || data.length === 0) return

    const panelCount = (showVolume ? 1 : 0) + (showRsi ? 1 : 0)
    const bottomMargin = panelCount === 0 ? 0.05 : panelCount === 1 ? 0.22 : 0.38
    cSeries.priceScale().applyOptions({ scaleMargins: { top: 0.05, bottom: bottomMargin } })

    const calc = calculateIndicators(data)
    const toggle = (key, shouldShow, createFn, getData) => {
      if (shouldShow && calc) {
        if (!key.current) key.current = createFn()
        key.current.setData(getData(calc).filter(p => p !== null && Number.isFinite(p.value)))
      } else if (!shouldShow && key.current) {
        try { chart.removeSeries(key.current) } catch {}
        key.current = null
      }
    }
    toggle(volumeSeriesRef, showVolume, () => {
      const s = chart.addHistogramSeries({ color: '#64748b', priceFormat: { type: 'volume' }, priceScaleId: 'volume', lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false })
      chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.80, bottom: 0 } })
      return s
    }, c => c.volume)
    toggle(rsiSeriesRef, showRsi, () => {
      const s = chart.addLineSeries({ color: '#a855f7', lineWidth: 1.5, title: 'RSI 14', priceScaleId: 'rsi', lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false })
      chart.priceScale('rsi').applyOptions({ scaleMargins: { top: 0.85, bottom: 0 }, entireTextOnly: true })
      return s
    }, c => c.rsi14)
  }, [showRsi, showVolume, chartVersion, tick, dataRef.current])

  // ─── EFFECT D: Indicators overlay ─────────────────────────────────────────
  useEffect(() => {
    const chart   = chartRef.current
    const cSeries = candleSeriesRef.current
    const data    = dataRef.current
    if (!chart || !cSeries || !data || data.length === 0) return

    // Descartar el tick síncrono que useChartData emite al INICIAR la carga de
    // un nuevo símbolo/timeframe. En ese momento dataRef.current todavía
    // contiene las velas del timeframe anterior; renderizar indicadores contra
    // esos datos crea series cuyos tiempos no coinciden con la serie de velas
    // que acaba de recibir setData en ChartRenderer, y lightweight-charts
    // termina lanzando "Value is null" de forma diferida.
    if (data === indicatorDataAtResetRef.current) return

    // ¿Es un run provocado SOLO por pan/zoom? Comparamos todas las entradas
    // del efecto excepto panTick con las del run anterior.
    const renderInputs = {
      activeIndicators, configs, syncMarkers, updateLabelPositions, palette,
      chartType, chartBg, isDark, chartVersion, tick, chochBosDataVersion,
      liquidityPoolsQuantumDataVersion,
    }
    const prevInputs = lastRegistryRenderInputsRef.current
    lastRegistryRenderInputsRef.current = renderInputs
    const panOnly = prevInputs != null
      && Object.keys(renderInputs).every((k) => renderInputs[k] === prevInputs[k])
    const anyActive = Object.values(activeIndicators).some(Boolean)
    // En pan puro, los indicadores con renderOnPan === false se conservan:
    // ni teardown ni rebuild. El resto se repinta como siempre.
    const renderIds = new Set(
      indicators
        .filter((ind) => activeIndicators[ind.id] && (!panOnly || ind.renderOnPan !== false))
        .map((ind) => ind.id)
    )

    // Clean up previous overlays
    if (!panOnly || !anyActive) {
      overlaySeriesRef.current.forEach(s => { try { chart.removeSeries(s) } catch {} })
      overlaySeriesRef.current = []
      overlayPriceLinesRef.current.forEach(l => { try { cSeries.removePriceLine(l) } catch {} })
      overlayPriceLinesRef.current = []
      overlayMarkersRef.current = []
      overlayLabelsRawRef.current = []
    } else {
      // Teardown selectivo: solo los elementos de los indicadores que se van a
      // repintar (o de los que ya no están activos). Los conservados quedan
      // intactos en refs y en el chart.
      overlaySeriesRef.current = overlaySeriesRef.current.filter(s => {
        const drop = !s.__indicatorId || renderIds.has(s.__indicatorId) || !activeIndicators[s.__indicatorId]
        if (drop) { try { chart.removeSeries(s) } catch {} }
        return !drop
      })
      overlayPriceLinesRef.current = overlayPriceLinesRef.current.filter(pl => {
        const drop = !pl.__indicatorId || renderIds.has(pl.__indicatorId) || !activeIndicators[pl.__indicatorId]
        if (drop) { try { cSeries.removePriceLine(pl) } catch {} }
        return !drop
      })
      overlayMarkersRef.current = overlayMarkersRef.current.filter(m => m.__indicatorId && !renderIds.has(m.__indicatorId) && activeIndicators[m.__indicatorId])
      overlayLabelsRawRef.current = overlayLabelsRawRef.current.filter(l => l.__indicatorId && !renderIds.has(l.__indicatorId) && activeIndicators[l.__indicatorId])
    }
    // Reset owner-aware del coloreado de velas (p.ej. supertrend_quantum):
    // solo se libera aquí si el dueño fue DESACTIVADO — este efecto puede
    // terminar antes de volver a invocar su render() (ver el return de
    // "anyActive" justo debajo). Si el dueño va a repintarse en este run ya NO
    // se pre-limpia: su render re-registrará (no-op si es idéntico, ver el
    // guard de setCandleColors) y el chequeo post-loop libera solo si el
    // render NO re-registró. Antes se limpiaba y bumpeaba en CADA run con el
    // dueño en renderIds — 2 bumps por tick y el churn de setData del
    // color-effect que movía el eje temporal solo. En runs pan-only con el
    // dueño conservado no se toca: el coloreado sigue vigente y nadie lo
    // repondría.
    const ccOwnerId = candleColorsRef.current?.ownerId
    if (ccOwnerId && !activeIndicators[ccOwnerId]) {
      candleColorsRef.current = null
      bumpColorVersion()
    }

    if (!anyActive) { syncMarkers(); updateLabelPositions(); return }

    // API que recibe cada indicador para dibujar — ver createChartDrawingApi
    // para el porqué de la validación (compartida con ChartPublicPage.jsx).
    const currentIndicatorIdRef = { current: null }
    const api = createChartDrawingApi({
      chart,
      candleSeries: cSeries,
      overlaySeriesRef,
      overlayPriceLinesRef,
      overlayMarkersRef,
      overlayLabelsRawRef,
      currentIndicatorIdRef,
      candleColorsRef,
      bumpColorVersion,
      colorRunId: ++colorRunRef.current,
    })

    // Ejecutar detect + render para cada indicador activo.
    // Diagnóstico temporal: guarda TODOS los indicadores activos de este
    // render (no sólo el último) para que un listener global de errores
    // (ver main.jsx) pueda congelar este snapshot completo en cuanto salte
    // el "Value is null" diferido, sin depender de DevTools.
    window.__allIndicatorRenders = []
    const newResults = {}
    for (const ind of indicators) {
      if (!activeIndicators[ind.id]) continue
      if (panOnly && ind.renderOnPan === false) continue
      // Config de la celda símbolo×TF activa (fallback a la global si la
      // celda no tiene override — _bySymbolTf, diseno-snapshot-chart-bot.md).
      const config = getConfig(ind.id, chartCellKey)
      const result = ind.detect ? ind.detect(data, config) : null
      newResults[ind.id] = result
      if (ind.id === 'ema_sma_quantum') {
        const notifUpdate = computeEmaSmaNotificationUpdate(data, config, `${selectedSymbolNorm}:${timeframe}`)
        if (notifUpdate) setConfig('ema_sma_quantum', { notifications: notifUpdate })
      }
      if (result && ind.render) {
        currentIndicatorIdRef.current = ind.id
        try {
          // Escanea recursivamente TODO el árbol de `result` (sin truncar)
          // buscando: valores numéricos no finitos, y tiempos duplicados o
          // desordenados dentro de cada array de puntos {time,...}. Evita
          // serializar miles de entradas a texto (que se truncaba y rompía
          // el diagnóstico anterior) calculando el resumen directamente.
          const badValues = []
          const timeArrayIssues = []
          const seen = new Set()
          const walk = (node, path) => {
            if (seen.has(node)) return
            if (node && typeof node === 'object') seen.add(node)
            if (Array.isArray(node)) {
              const times = node.map(n => n && typeof n === 'object' ? n.time : undefined).filter(t => t !== undefined)
              if (times.length > 1) {
                const dupIdx = times.findIndex((t, i) => i > 0 && t === times[i - 1])
                const outOfOrderIdx = times.findIndex((t, i) => i > 0 && t < times[i - 1])
                if (dupIdx !== -1 || outOfOrderIdx !== -1) {
                  timeArrayIssues.push({ path, count: times.length, first: times[0], last: times[times.length - 1], dupIdx, outOfOrderIdx })
                }
              }
              node.forEach((v, i) => walk(v, `${path}[${i}]`))
            } else if (node && typeof node === 'object') {
              for (const [k, v] of Object.entries(node)) {
                if (typeof v === 'number' && !Number.isFinite(v)) badValues.push({ path: `${path}.${k}`, value: v })
                else walk(v, `${path}.${k}`)
              }
            }
          }
          walk(result, 'result')
          const seriesCountBefore = overlaySeriesRef.current.length
          window.__allIndicatorRenders.push({
            id: ind.id,
            timeframe,
            symbol: selectedSymbol,
            dataLength: data.length,
            firstTime: data[0]?.time,
            lastTime: data[data.length - 1]?.time,
            candleTimesDupOrOutOfOrder: (() => {
              for (let i = 1; i < data.length; i++) {
                if (data[i].time <= data[i - 1].time) return { i, prev: data[i - 1].time, curr: data[i].time }
              }
              return null
            })(),
            config,
            badValuesCount: badValues.length,
            badValuesSample: badValues.slice(0, 10),
            timeArrayIssues,
            seriesCountBeforeThisIndicator: seriesCountBefore,
          })
        } catch (e) {
          window.__allIndicatorRenders.push({ id: ind.id, error: String(e), stack: e?.stack })
        }
        ind.render(chart, cSeries, result, config, api)
      }
    }
    // Chequeo post-loop del coloreado (mismo criterio que ChartPublicPage): si
    // el dueño se repintó en este run pero su render NO re-registró colores,
    // el registry queda obsoleto — se libera y se restaura la paleta. Si
    // re-registró (incluso no-op idéntico), su runId es el de este run.
    const ccAfter = candleColorsRef.current
    if (ccAfter?.ownerId && renderIds.has(ccAfter.ownerId) && ccAfter.runId !== colorRunRef.current) {
      candleColorsRef.current = null
      bumpColorVersion()
    }
    // En pan puro, los indicadores conservados no están en newResults: merge
    // para que sus badges no parpadeen. En runs completos se reemplaza.
    if (panOnly) {
      setIndResults(prev => ({ ...prev, ...newResults }))
    } else {
      setIndResults(newResults)
    }
    window.__lastRenderMeta = {
      timeframe,
      symbol: selectedSymbol,
      totalOverlaySeries: overlaySeriesRef.current.length,
      activeIndicatorIds: indicators.filter(ind => activeIndicators[ind.id]).map(ind => ind.id),
      hasSelectedPosition: !!selectedPosition,
      timestamp: Date.now(),
    }

    syncMarkers()
    updateLabelPositions()
    // `tick` (aka dataVersion) is read implicitly via dataRef.current above; it
    // must be a dependency so this effect re-runs whenever fresh candle data
    // lands (new timeframe/symbol, or a periodic reload). Without it, registry
    // indicators (patron_ruptura_quantum, vwap, ...) kept rendering
    // against whatever candles were loaded the last time some *other*
    // dependency changed — e.g. still showing 1h entry/TP/SL boxes after
    // switching to 15m.
  }, [activeIndicators, configs, syncMarkers, updateLabelPositions, palette, chartType, chartBg, isDark, chartVersion, tick, chochBosDataVersion, liquidityPoolsQuantumDataVersion, panTick])

  // ─── ICT/SMC indicators ───────────────────────────────────────────────────────
  // Use first-candle timestamp as dep: changes when timeframe or symbol loads new candles,
  // but NOT on every tick (to avoid refetching on each new close).
  const ictFrom = candleData[0]?.time ?? 0

  const ictSignalParams = useMemo(() => {
    const sig = debouncedIctConfig.signal ?? {}
    const mtf = debouncedIctConfig.multiTimeframe ?? {}
    return {
      min_score:                 sig.minScore ?? 4,
      require_sweep:             sig.requireSweep ?? false,
      reject_asia:               sig.rejectAsia ?? false,
      reject_equilibrium:        sig.rejectEquilibrium ?? false,
      require_premium_discount:  sig.requirePremiumDiscount ?? false,
      min_rr:                    sig.minRR ?? 1.2,
      use_structural_sl_tp:      sig.useStructuralSLTP ?? true,
      require_htf_alignment:     mtf.requireHTFAlignment ?? false,
      poi_proximity_pct:         sig.poiProximityPct ?? 0.5,
      recent_structure_bars:     sig.recentStructureBars ?? 15,
      sl_tp_recency_bars:        sig.slTpRecencyBars ?? 20,
      use_volume:                sig.useVolume ?? true,
      vol_spike_threshold:       sig.volSpikeThreshold ?? 1.5,
    }
  }, [debouncedIctConfig.signal, debouncedIctConfig.multiTimeframe])

  const ictFvgParams = useMemo(() => ({
    fvg_max_per_tf:        debouncedIctConfig.fvg?.maxFVG ?? 10,
    fvg_max_ifvg_per_tf:   debouncedIctConfig.fvg?.maxIFVG ?? 5,
    fvg_min_size_atr_mult: debouncedIctConfig.fvg?.minSizeATRMult ?? 0.5,
    fvg_extension_bars:    debouncedIctConfig.fvg?.extensionBars ?? 5,
  }), [debouncedIctConfig.fvg])

  const ictObParams = useMemo(() => ({
    ob_max_per_tf:        debouncedIctConfig.ob?.maxPerTf ?? 10,
    ob_lookback:          debouncedIctConfig.ob?.lookback ?? 30,
    ob_extension_bars:    debouncedIctConfig.ob?.extensionBars ?? 10,
    ob_mitigation:        debouncedIctConfig.ob?.mitigation ?? 'wick',
    ob_min_volume_grade:  debouncedIctConfig.ob?.minVolumeGrade ?? 1.2,
  }), [debouncedIctConfig.ob])

  const ictStructureParams = useMemo(() => ({
    structure_swing_len:      debouncedIctConfig.structure?.swingLen ?? 10,
    structure_internal_len:   debouncedIctConfig.structure?.internalLen ?? 5,
    structure_min_atr_mult:   debouncedIctConfig.structure?.minMoveATRMult ?? 0.5,
    show_strong_weak:         debouncedIctConfig.structure?.showStrongWeak ?? true,
  }), [debouncedIctConfig.structure])

  const ictFibParams = useMemo(() => ({
    fib_enabled:    debouncedIctConfig.fib?.showFib ?? true,
    fib_show_ote:   debouncedIctConfig.fib?.showOTE ?? true,
  }), [debouncedIctConfig.fib])

  const ictHtfParams = useMemo(() => {
    const mtf = debouncedIctConfig.multiTimeframe ?? {}
    return {
      enable_htf:      mtf.enableHTF ?? true,
      htf_resolution:  mtf.htfResolution || undefined,
      htf_ema_len:     mtf.htfEMALen ?? 50,
    }
  }, [debouncedIctConfig.multiTimeframe])

  // Playback ICT: use the pre-calculated snapshot of the latest visible bar.
  useEffect(() => {
    if (!pb.isPlayback || !showICT || candleData.length === 0) {
      if (pb.isPlayback) { setIctData(null); setIctSignal(null) }
      return
    }
    const snap = candleData[candleData.length - 1]?.indicators
    setIctData(snap?.ict_output || null)
    setIctSignal(snap?.signal_output || null)
  }, [pb.isPlayback, showICT, candleData])

  useEffect(() => {
    if (pb.isPlayback || !showICT || candleData.length === 0 || ictFrom === 0) {
      if (!pb.isPlayback) { setIctData(null); setIctSignal(null) }
      setIctMainLoading(false)
      return
    }
    const reqId = ++ictReqIdRef.current
    setIctMainLoading(true)
    const ictRange = ensureIndicatorRange(candleData, timeframe, 60)
    if (ictRange === null) return
    const params = {
      symbol:     selectedSymbol,
      resolution: timeframe,
      from_time:  ictRange.from_time,
      to_time:    ictRange.to_time,
      ...ictSignalParams,
      ...ictFvgParams,
      ...ictObParams,
      ...ictStructureParams,
      ...ictFibParams,
      ...ictHtfParams,
    }
    Promise.all([
      chartingService.getICT(params),
      chartingService.getICTSignal(params),
    ]).then(([ictRes, sigRes]) => {
      if (reqId !== ictReqIdRef.current) return // superseded by a newer symbol/timeframe request
      const data = ictRes.data ?? ictRes
      setIctData(data)
      setChochBosQuantumData(data)
      setChochBosDataVersion(v => v + 1)
      const sig = sigRes.data ?? sigRes
      setIctSignal(sig?.direction && sig.direction !== 'none' ? sig : null)
    }).catch((err) => {
      // Pipeline de datos: silencio en prod por defecto vía logger gateado (visible con ?debug=1 — G.5).
      debugError('[ChartPage] fetch ICT/signal falló:', err?.status ?? '', err?.message ?? err)
    }).finally(() => {
      if (reqId === ictReqIdRef.current) setIctMainLoading(false)
    })
  }, [showICT, selectedSymbol, timeframe, ictFrom, ictSignalParams, ictFvgParams, ictObParams, ictStructureParams, ictFibParams, ictHtfParams, candleData.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── CHoCH/BOS Quantum: datos del endpoint ICT/SMC con FVG/OB/Fib/HTF apagados ─
  // Si ICT/SMC Quantum está activo reutilizamos su payload; si no, pedimos una
  // versión ligera del mismo endpoint para evitar peticiones duplicadas.
  // MTF: con timeframes adicionales activos NO se reutiliza el payload de ICT
  // (sus parámetros de estructura pueden diferir); se hace fetch propio con
  // una petición extra por TF adicional y merge etiquetando líneas con `_mtf`.
  useEffect(() => {
    const showChochBos = activeIndicators['choch_bos_quantum'] === true
    if (pb.isPlayback || !showChochBos || candleData.length === 0 || ictFrom === 0) {
      if (!pb.isPlayback) { setChochBosQuantumData(null); setChochBosDataVersion(0) }
      return
    }
    const sc = debouncedChochBosConfig.structure ?? {}
    const htfTfs = sanitizeHtfTimeframes(debouncedChochBosConfig.htfTimeframes ?? [], timeframe)
    if (showICT && ictData && htfTfs.length === 0) {
      setChochBosQuantumData(ictData)
      setChochBosDataVersion(v => v + 1)
      return
    }
    const chochBosRange = ensureIndicatorRange(candleData, timeframe, 60)
    if (chochBosRange === null) return
    const params = {
      symbol: selectedSymbol,
      resolution: timeframe,
      from_time: chochBosRange.from_time,
      to_time: chochBosRange.to_time,
      structure_swing_len: sc.swingLen ?? 10,
      structure_internal_len: sc.internalLen ?? 5,
      structure_min_atr_mult: sc.minMoveATRMult ?? 0.5,
      fvg_max_per_tf: 0,
      fvg_max_ifvg_per_tf: 0,
      ob_max_per_tf: 0,
      fib_enabled: false,
      enable_htf: false,
    }
    const reqId = ++chochBosReqIdRef.current
    const fetchChochBos = (p) => chartingService.getICT(p).then(res => res.data ?? res)
    fetchMtfIndicatorData(fetchChochBos, params, htfTfs, ['lines'])
      .then(data => {
        if (reqId !== chochBosReqIdRef.current) return
        setChochBosQuantumData(data)
        setChochBosDataVersion(v => v + 1)
      })
      .catch((err) => {
        // Pipeline de datos: silencio en prod por defecto vía logger gateado (visible con ?debug=1 — G.5). Sin
        // fail-soft aquí: el indicador se re-pide en el siguiente cambio de
        // deps (símbolo/timeframe/config), y el endpoint ICT principal ya
        // tiene su propio fail-soft en su efecto.
        debugError('[ChartPage] fetch CHoCH/BOS falló:', err?.status ?? '', err?.message ?? err)
      })
  }, [pb.isPlayback, activeIndicators, showICT, ictData, selectedSymbol, timeframe, ictFrom, debouncedChochBosConfig])

  // ─── Order Blocks Quantum fetch ──────────────────────────────────────────────
  const orderBlocksFailCountRef = useRef(0)
  const orderBlocksLastFailAtRef = useRef(0)
  const orderBlocksDisabledRef = useRef(false)
  const ORDER_BLOCKS_MAX_FAILS = 5
  const ORDER_BLOCKS_BACKOFF_MS = 30000

  useEffect(() => {
    const showOrderBlocks = activeIndicators['order_blocks_quantum'] === true
    if (pb.isPlayback || !showOrderBlocks || candleData.length === 0 || ictFrom === 0) {
      if (!pb.isPlayback) { setOrderBlocksQuantumData(null); setOrderBlocksDataVersion(0) }
      return
    }
    if (orderBlocksDisabledRef.current) return
    const now = Date.now()
    if (orderBlocksFailCountRef.current > 0 && now - orderBlocksLastFailAtRef.current < ORDER_BLOCKS_BACKOFF_MS) {
      return
    }
    const cfg = debouncedOrderBlocksConfig ?? {}
    const reqId = ++orderBlocksReqIdRef.current
    const controller = new AbortController()
    const obRange = ensureIndicatorRange(candleData, timeframe, 60)
    if (obRange === null) return
    const params = {
      symbol: selectedSymbol,
      resolution: timeframe,
      from_time: obRange.from_time,
      to_time: obRange.to_time,
      detection_mode: cfg.detectionMode ?? 'standard',
      swing_length: cfg.swingLength ?? 5,
      mitigation: cfg.mitigation ?? 'wick',
      use_body: cfg.useBody ?? false,
      max_ob: cfg.maxVisibleZones ?? 5,
      show_breakers: cfg.showBreakers ?? false,
      show_mitigated: cfg.showMitigated ?? true,
      chop_control: cfg.chopControl ?? true,
      min_size_pct: cfg.minBlockSizePercent ?? 0,
      box_length_bars: cfg.boxLengthBars ?? 50,
    }
    // MTF: una petición extra por cada TF adicional activo (solo TFs mayores
    // que el del chart, máx. 2); merge etiquetando zonas con `_mtf` (utils/mtf.js).
    const htfTfs = sanitizeHtfTimeframes(cfg.htfTimeframes ?? [], timeframe)
    const fetchOb = (p) => chartingService
      .getOrderBlocks(p, { signal: controller.signal, timeout: 20000 })
      .then(res => res.data ?? res)
    fetchMtfIndicatorData(fetchOb, params, htfTfs, ['obs', 'mitigated_obs', 'breakers'])
      .then(data => {
        if (reqId !== orderBlocksReqIdRef.current) return
        orderBlocksFailCountRef.current = 0
        orderBlocksLastFailAtRef.current = 0
        setOrderBlocksQuantumData(data)
        setOrderBlocksDataVersion(v => v + 1)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        orderBlocksFailCountRef.current += 1
        orderBlocksLastFailAtRef.current = Date.now()
        if (orderBlocksFailCountRef.current >= ORDER_BLOCKS_MAX_FAILS) {
          orderBlocksDisabledRef.current = true
        }
      })
    return () => controller.abort()
  }, [pb.isPlayback, activeIndicators, selectedSymbol, timeframe, ictFrom, debouncedOrderBlocksConfig])

  // Reset de fail-soft al cambiar de contexto.
  useEffect(() => {
    orderBlocksFailCountRef.current = 0
    orderBlocksLastFailAtRef.current = 0
    orderBlocksDisabledRef.current = false
  }, [selectedSymbol, timeframe, indState.activeRegistry])

  // ─── Mitigation Block Quantum fetch ──────────────────────────────────────────
  const mbQuantumFailCountRef = useRef(0)
  const mbQuantumLastFailAtRef = useRef(0)
  const mbQuantumDisabledRef = useRef(false)
  const MB_QUANTUM_MAX_FAILS = 5
  const MB_QUANTUM_BACKOFF_MS = 30000

  useEffect(() => {
    const showMitigationBlock = activeIndicators['mitigation_block_quantum'] === true
    if (pb.isPlayback || !showMitigationBlock || candleData.length === 0 || ictFrom === 0) {
      if (!pb.isPlayback) { setMitigationBlockQuantumData(null); setMitigationBlockDataVersion(0) }
      return
    }
    if (mbQuantumDisabledRef.current) return
    const now = Date.now()
    if (mbQuantumFailCountRef.current > 0 && now - mbQuantumLastFailAtRef.current < MB_QUANTUM_BACKOFF_MS) {
      return
    }
    const cfg = debouncedMbQuantumConfig ?? {}
    const reqId = ++mbQuantumReqIdRef.current
    const controller = new AbortController()
    const mbRange = ensureIndicatorRange(candleData, timeframe, 60)
    if (mbRange === null) return
    const params = {
      symbol: selectedSymbol,
      resolution: timeframe,
      from_time: mbRange.from_time,
      to_time: mbRange.to_time,
      swing_length: cfg.swingLength ?? 5,
      mitigation: cfg.mitigation ?? 'wick',
      max_mb: cfg.maxVisibleZones ?? 5,
      show_mitigated: cfg.showMitigated ?? true,
      show_invalidated: cfg.showInvalidated ?? false,
      weight_speed: cfg.weightSpeed ?? 0.40,
      weight_volume: cfg.weightVolume ?? 0.35,
      weight_htf: cfg.weightHtf ?? 0.25,
      speed_horizon: cfg.speedHorizon ?? 50,
      volume_lookback: cfg.volumeLookback ?? 20,
      htf_timeframe: cfg.htfScoreTimeframe ?? '1d',
      htf_lookback: cfg.htfScoreLookback ?? 20,
    }
    // MTF: una petición extra por cada TF adicional activo (solo TFs mayores
    // que el del chart, máx. 2); merge etiquetando zonas con `_mtf` (utils/mtf.js).
    const htfTfs = sanitizeHtfTimeframes(cfg.htfTimeframes ?? [], timeframe)
    const fetchMb = (p) => chartingService
      .getMitigationBlocks(p, { signal: controller.signal, timeout: 20000 })
      .then(res => res.data ?? res)
    fetchMtfIndicatorData(fetchMb, params, htfTfs, ['mbs', 'mitigated_mbs', 'invalidated_mbs'])
      .then(data => {
        if (reqId !== mbQuantumReqIdRef.current) return
        mbQuantumFailCountRef.current = 0
        mbQuantumLastFailAtRef.current = 0
        setMitigationBlockQuantumData(data)
        setMitigationBlockDataVersion(v => v + 1)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        mbQuantumFailCountRef.current += 1
        mbQuantumLastFailAtRef.current = Date.now()
        if (mbQuantumFailCountRef.current >= MB_QUANTUM_MAX_FAILS) {
          mbQuantumDisabledRef.current = true
        }
      })
    return () => controller.abort()
  }, [pb.isPlayback, activeIndicators, selectedSymbol, timeframe, ictFrom, debouncedMbQuantumConfig])

  // ─── Liquidity Pools Quantum fetch ───────────────────────────────────────────
  // Endpoint dedicado /charting/liquidity-pools (canon v1.0): SOLO symbol,
  // resolution, from_time, to_time — sin config params (la lógica la fija el
  // canon y la aplica el backend: caps 3+3 por lado, ciclo de vida del
  // barrido). Sin MTF para este indicador.
  const lpQuantumFailCountRef = useRef(0)
  const lpQuantumLastFailAtRef = useRef(0)
  const lpQuantumDisabledRef = useRef(false)
  const LP_QUANTUM_MAX_FAILS = 5
  const LP_QUANTUM_BACKOFF_MS = 30000
  const lpQuantumReqIdRef = useRef(0)

  useEffect(() => {
    const active = activeIndicators['liquidity_pools_quantum'] === true
    if (pb.isPlayback || !active || candleData.length === 0 || ictFrom === 0) {
      if (!pb.isPlayback) { setLiquidityPoolsQuantumData(null); setLiquidityPoolsQuantumDataVersion(0) }
      return
    }
    if (lpQuantumDisabledRef.current) return
    const now = Date.now()
    if (lpQuantumFailCountRef.current > 0 && now - lpQuantumLastFailAtRef.current < LP_QUANTUM_BACKOFF_MS) {
      return
    }
    const reqId = ++lpQuantumReqIdRef.current
    const controller = new AbortController()
    const lpRange = ensureIndicatorRange(candleData, timeframe, 60)
    if (lpRange === null) return
    const params = {
      symbol: selectedSymbol,
      resolution: timeframe,
      from_time: lpRange.from_time,
      to_time: lpRange.to_time,
    }
    chartingService
      .getLiquidityPools(params, { signal: controller.signal, timeout: 20000 })
      .then(res => res.data ?? res)
      .then(data => {
        if (reqId !== lpQuantumReqIdRef.current) return
        if (!data || !Array.isArray(data.pools)) throw new Error('respuesta sin pools')
        lpQuantumFailCountRef.current = 0
        lpQuantumLastFailAtRef.current = 0
        // Respuesta COMPLETA: pools + cisd_events + stats.
        setLiquidityPoolsQuantumData(data)
        setLiquidityPoolsQuantumDataVersion(v => v + 1)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        lpQuantumFailCountRef.current += 1
        lpQuantumLastFailAtRef.current = Date.now()
        if (lpQuantumFailCountRef.current >= LP_QUANTUM_MAX_FAILS) {
          lpQuantumDisabledRef.current = true
        }
      })
    return () => controller.abort()
  }, [pb.isPlayback, activeIndicators, selectedSymbol, timeframe, ictFrom])

  // Reset de fail-soft al cambiar de contexto.
  useEffect(() => {
    lpQuantumFailCountRef.current = 0
    lpQuantumLastFailAtRef.current = 0
    lpQuantumDisabledRef.current = false
  }, [selectedSymbol, timeframe, indState.activeRegistry])

  // Reset de fail-soft al cambiar de contexto.
  useEffect(() => {
    mbQuantumFailCountRef.current = 0
    mbQuantumLastFailAtRef.current = 0
    mbQuantumDisabledRef.current = false
  }, [selectedSymbol, timeframe, indState.activeRegistry])

  // ─── ICT historical signal scan ──────────────────────────────────────────────
  const showHistory = debouncedIctConfig.signal?.showHistory ?? true
  useEffect(() => {
    if (pb.isPlayback || !showICT || !showHistory || candleData.length === 0 || ictFrom === 0) {
      setIctHistoryData(null)
      setIctHistoryLoading(false)
      return
    }
    const reqId = ++ictHistoryReqIdRef.current
    setIctHistoryLoading(true)
    const params = {
      symbol:     selectedSymbol,
      resolution: timeframe,
      from_time:  candleTimeToSeconds(candleData[0].time),
      to_time:    candleTimeToSeconds(candleData[candleData.length - 1].time),
      ...ictSignalParams,
      ...ictFvgParams,
      ...ictObParams,
      ...ictStructureParams,
      ...ictFibParams,
      ...ictHtfParams,
    }
    chartingService.getICTSignalsHistory(params)
      .then(res => {
        if (reqId !== ictHistoryReqIdRef.current) return // superseded by a newer symbol/timeframe request
        setIctHistoryData(res.data ?? res)
      })
      .catch((err) => {
        // Pipeline de datos: silencio en prod por defecto vía logger gateado (visible con ?debug=1 — G.5).
        debugError('[ChartPage] fetch ICT signals history falló:', err?.status ?? '', err?.message ?? err)
      }).finally(() => {
        if (reqId === ictHistoryReqIdRef.current) setIctHistoryLoading(false)
      })
  }, [pb.isPlayback, showICT, showHistory, selectedSymbol, timeframe, ictFrom, ictSignalParams, ictFvgParams, ictObParams, ictStructureParams, ictFibParams, ictHtfParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Keep trading refs in sync ────────────────────────────────────────────────
  useEffect(() => { tradingModeRef.current = tradingMode }, [tradingMode])
  useEffect(() => { tradeEntryPriceRef.current = tradeEntryPrice }, [tradeEntryPrice])

  // ─── Update trade entry line when price changes (without chart recreation) ────
  useEffect(() => {
    const series = candleSeriesRef.current
    if (!series) return
    if (tradeEntryLineRef.current) {
      try { series.removePriceLine(tradeEntryLineRef.current) } catch {}
      tradeEntryLineRef.current = null
    }
    if (tradeEntryPrice) {
      try {
        tradeEntryLineRef.current = series.createPriceLine({
          price: tradeEntryPrice,
          color: '#f59e0b',
          lineWidth: 2,
          lineStyle: 1,
          axisLabelVisible: true,
          title: 'Entry',
        })
      } catch {}
    }
  }, [tradeEntryPrice])

  // ─── Load trade accounts when panel opens ─────────────────────────────────────
  useEffect(() => {
    if (!showTradePanel || tradeAccounts.length > 0) return
    manualTradeService.getAccounts()
      .then(res => {
        const accs = res.data?.accounts || []
        setTradeAccounts(accs)
        if (accs.length > 0) {
          setTradeAccountId(accs[0].id)
          setTradeAccountType(accs[0].type || 'real')
        }
      })
      .catch(() => {})
  }, [showTradePanel])

  // ─── Execute trade from chart ──────────────────────────────────────────────────
  const executeTrade = async (action) => {
    if (!tradeAccountId) return
    setTradeLoading(true)
    setTradeFeedback(null)
    const payload = {
      symbol: toCcxtSymbol(selectedSymbol),
      action,
      leverage: tradeLeverage,
      position_sizing_type: tradeSizingType,
      position_value: tradeSizingValue,
      initial_sl_percentage: tradeSlPct,
      take_profits: tradeTPs.filter(tp => tp.profit_percent && tp.close_percent),
      trailing_config: tradeTrailing.enabled ? { enabled: true, activation_profit: tradeTrailing.activation_profit, callback_rate: tradeTrailing.callback_rate } : null,
      breakeven_config: tradeBreakeven.enabled ? { enabled: true, activation_profit: tradeBreakeven.activation_profit, lock_profit: tradeBreakeven.lock_profit } : null,
      dynamic_sl_config: tradeDynamicSL.enabled ? { enabled: true, step_percent: tradeDynamicSL.step_percent, max_steps: tradeDynamicSL.max_steps } : null,
      order_type: tradeEntryPrice ? 'limit' : 'market',
    }
    if (tradeEntryPrice) payload.limit_price = tradeEntryPrice
    if (tradeAccountType === 'paper') payload.paper_balance_id = tradeAccountId
    else payload.exchange_account_id = tradeAccountId

    try {
      await manualTradeService.execute(payload)
      const isLimit = !!tradeEntryPrice
      setTradeFeedback({ type: 'ok', msg: `${action.toUpperCase()} enviado — procesando…` })
      setTradeEntryPrice(null)
      setTradingMode(false)

      // Celery processes the trade async — short wait then poll
      await new Promise(r => setTimeout(r, 800))

      const posRes = await positionsService.unified(true)
      const open   = Array.isArray(posRes?.data) ? posRes.data : Array.isArray(posRes) ? posRes : []
      setPositions(prev => [...open, ...prev.filter(p => p.status === 'closed')])

      // Auto-select the new position and show it on chart
      const newPos = open.find(p =>
        normalizeSymbol(toCcxtSymbol(p.symbol)) === selectedSymbolNorm &&
        p.status === 'open' && p.side === action
      )
      if (newPos) {
        setSelectedPosition(newPos)
        setSidebarVisible(v => ({ ...v, openPos: true }))
        setTradeFeedback({ type: 'ok', msg: `${action.toUpperCase()} abierto @ ${formatPrice(newPos.entry_price)}` })
      } else if (isLimit) {
        setTradeFeedback({ type: 'ok', msg: `Limit ${action.toUpperCase()} pendiente de ejecución` })
      } else {
        setTradeFeedback({ type: 'ok', msg: `${action.toUpperCase()} enviado — revisa posiciones` })
      }
    } catch (err) {
      setTradeFeedback({ type: 'error', msg: err.response?.data?.detail || 'Error al ejecutar' })
    } finally {
      setTradeLoading(false)
    }
  }

  // ─── Drag price lines ────────────────────────────────────────────────────────
  const refreshSelectedPosition = async (posId) => {
    const posRes = await positionsService.unified(true)
    const open = Array.isArray(posRes?.data) ? posRes.data : Array.isArray(posRes) ? posRes : []
    const updated = open.find(p => p.id === posId)
    if (updated) {
      setSelectedPosition(updated)
      setPositions(prev => prev.map(p => p.id === posId ? updated : p))
    }
  }

  const handleChartMouseDown = useCallback((e) => {
    if (!selectedPosition || !candleSeriesRef.current) return
    const y = e.nativeEvent.offsetY
    const THRESHOLD = 14
    const tryLine = (price, lineId) => {
      if (!price) return false
      const lineY = candleSeriesRef.current.priceToCoordinate(parseFloat(price))
      if (lineY !== null && Math.abs(y - lineY) < THRESHOLD) {
        draggingLineRef.current = lineId
        dragNewPriceRef.current = parseFloat(price)
        e.preventDefault()
        // Freeze chart pan/zoom so the drag only moves the price line
        if (chartRef.current) chartRef.current.applyOptions({ handleScroll: false, handleScale: false })
        return true
      }
      return false
    }
    if (tryLine(selectedPosition.current_sl_price, 'sl')) return
    ;(selectedPosition.current_tp_prices || []).forEach((tp, i) => {
      tryLine(tp?.price ?? tp, `tp_${i}`)
    })
  }, [selectedPosition])

  const handleChartMouseMove = useCallback((e) => {
    if (!draggingLineRef.current || !candleSeriesRef.current) return
    const price = candleSeriesRef.current.coordinateToPrice(e.nativeEvent.offsetY)
    if (!price || price <= 0) return
    dragNewPriceRef.current = price
    if (draggingLineRef.current === 'sl' && slPriceLineRef.current) {
      slPriceLineRef.current.applyOptions({ price })
    } else if (draggingLineRef.current?.startsWith('tp_')) {
      const idx = parseInt(draggingLineRef.current.split('_')[1])
      if (tpPriceLinesRef.current[idx]) tpPriceLinesRef.current[idx].applyOptions({ price })
    }
  }, [])

  const handleChartMouseUp = useCallback(async () => {
    const lineId = draggingLineRef.current
    const newPrice = dragNewPriceRef.current
    draggingLineRef.current = null
    // Restore chart interaction after drag
    if (chartRef.current) chartRef.current.applyOptions({ handleScroll: { vertTouchDrag: false }, handleScale: true })
    if (!lineId || !newPrice || !selectedPosition) return

    const getOriginalPrice = () => {
      if (lineId === 'sl') return parseFloat(selectedPosition.current_sl_price)
      if (lineId.startsWith('tp_')) {
        const idx = parseInt(lineId.split('_')[1])
        const tp = (selectedPosition.current_tp_prices || [])[idx]
        return parseFloat(tp?.price ?? tp)
      }
      return null
    }

    const originalPrice = getOriginalPrice()
    if (originalPrice !== null && Math.abs(newPrice - originalPrice) < 1e-9) return

    const confirmed = window.confirm('¿Seguro de modificar el SL/TP de la posición abierta?')
    if (!confirmed) {
      // Revertir visualmente la línea a su precio original
      if (lineId === 'sl' && slPriceLineRef.current) {
        slPriceLineRef.current.applyOptions({ price: originalPrice })
      } else if (lineId.startsWith('tp_')) {
        const idx = parseInt(lineId.split('_')[1])
        if (tpPriceLinesRef.current[idx]) tpPriceLinesRef.current[idx].applyOptions({ price: originalPrice })
      }
      return
    }

    try {
      if (lineId === 'sl') {
        await positionsService.updateSL(selectedPosition.id, { sl_price: newPrice })
      } else if (lineId.startsWith('tp_')) {
        const idx = parseInt(lineId.split('_')[1])
        const newTPs = (selectedPosition.current_tp_prices || []).map((tp, i) =>
          i === idx ? { ...(typeof tp === 'object' ? tp : { price: tp }), price: newPrice } : tp
        )
        await positionsService.updateTP(selectedPosition.id, { tp_levels: newTPs })
      }
      await refreshSelectedPosition(selectedPosition.id)
    } catch (err) {
      debugError('Error updating price line:', err)
    }
  }, [selectedPosition])

  // ─── Strategy edit panel ─────────────────────────────────────────────────────
  const applyStrategyChanges = async (pos) => {
    setStrategyLoading(true)
    try {
      const bot = bots.find(b => b.id === pos.bot_id)
      const trailing = strategyEdit.trailing
      await positionsService.updateStrategy(pos.id, {
        take_profits: strategyEdit.tps.filter(t => t.profit_percent > 0 && t.close_percent > 0),
        trailing_config: { enabled: trailing.enabled, activation_profit: Number(trailing.activation_profit), callback_rate: Number(trailing.callback_rate) },
        breakeven_config: bot?.breakeven_config || undefined,
        dynamic_sl_config: bot?.dynamic_sl_config || undefined,
      })
      await refreshSelectedPosition(pos.id)
      setShowStrategyPanel(false)
    } catch (err) {
      debugError('Error applying strategy:', err)
    } finally {
      setStrategyLoading(false)
    }
  }

  const closePosition = async (pos) => {
    try {
      await positionsService.close(pos.id)
      const posRes = await positionsService.unified(true)
      const open = Array.isArray(posRes?.data) ? posRes.data : Array.isArray(posRes) ? posRes : []
      setPositions(prev => [...open, ...prev.filter(p => p.status === 'closed')])
      if (selectedPosition?.id === pos.id) setSelectedPosition(null)
    } catch (err) {
      debugError('Error cerrando posición:', err)
    }
  }

  // ─── UI helpers ──────────────────────────────────────────────────────────────

  if (loading) return <div className="flex justify-center items-center h-64"><LoadingSpinner /></div>

  const openForSymbol   = positions.filter(p => p.status === 'open'   && normalizeSymbol(toCcxtSymbol(p.symbol)) === selectedSymbolNorm)
  const closedForSymbol = positions.filter(p => p.status === 'closed' && normalizeSymbol(toCcxtSymbol(p.symbol)) === selectedSymbolNorm)

  // Outer container: fixed viewport (or fullscreen modal)
  const outerClass = isFullscreen
    ? 'fixed inset-0 z-50 overflow-hidden flex flex-col bg-slate-100 dark:bg-gray-950'
    : 'fixed top-0 right-0 bottom-0 overflow-hidden flex flex-col bg-slate-100 dark:bg-gray-950 transition-all duration-200 pb-20 md:pb-0 z-10'
  const outerStyle = isFullscreen ? {} : { left: sidebarOpen ? '224px' : '64px' }

  return (
    <div className={outerClass} style={outerStyle}>
      <div className="flex flex-col h-full p-3 gap-2 overflow-hidden">

        {/* ── Toolbar ────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">

          {/* Title */}
          <div className="flex items-center gap-2 mr-1">
            <BarChart3 className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm font-bold text-slate-900 dark:text-white hidden sm:block">Chart</span>
          </div>

          {/* Symbol selector */}
          <SymbolSelector
            selectedSymbol={selectedSymbol}
            onSelect={(sym, source) => {
              setSelectedSymbol(sym)
              setDataSource(resolveSelectionSource(sym, source))
              tradingSignalsService.list({ symbol: sym, days: 7, limit: 100 })
                .then(res => setSignals(res.data?.signals || []))
                .catch(() => setSignals([]))
            }}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
            fetchCryptoMarkets={exchangeAccountsService.marketsByExchange}
            searchSymbols={chartingService.searchSymbols}
          />

          {/* Timeframe dropdown */}
          <TimeframeSelector
            timeframes={TIMEFRAMES}
            value={timeframe}
            onChange={(tf) => { setTimeframe(tf); setShowTfMenu(false) }}
            showMenu={showTfMenu}
            onToggleMenu={() => setShowTfMenu(!showTfMenu)}
            menuRef={tfMenuRef}
          />

          <CandleCountdown timeframe={timeframe} />

          {/* Chart type selector */}
          <div className="flex items-center gap-0.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-0.5">
            {[
              { type: 'candles', icon: <CandlestickChart size={13} />, title: 'Velas' },
              { type: 'line',    icon: <LineChart size={13} />,        title: 'Línea' },
              { type: 'area',    icon: <AreaChart size={13} />,        title: 'Área' },
            ].map(({ type, icon, title }) => (
              <button key={type} title={title} onClick={() => setChartType(type)}
                className={`p-1.5 rounded-md transition-colors ${
                  chartType === type ? 'bg-blue-600 text-white' : 'text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700'
                }`}>
                {icon}
              </button>
            ))}
          </div>

          {/* Palette selector */}
          <div className="relative" ref={paletteMenuRef}>
            <button
              onClick={() => setShowPaletteMenu(!showPaletteMenu)}
              title="Paleta de colores"
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-750"
            >
              <div className="flex gap-0.5">
                <div className="w-2 h-3 rounded-sm" style={{ backgroundColor: pal.up }} />
                <div className="w-2 h-3 rounded-sm" style={{ backgroundColor: pal.down }} />
              </div>
              <ChevronDown size={11} className="text-slate-400" />
            </button>
            {showPaletteMenu && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                {Object.entries(PALETTES).map(([key, p]) => (
                  <button key={key} onClick={() => { setPalette(key); setShowPaletteMenu(false) }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-gray-700 ${palette === key ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600' : 'text-slate-700 dark:text-gray-300'}`}>
                    <div className="flex gap-0.5">
                      <div className="w-3 h-4 rounded-sm" style={{ backgroundColor: p.up }} />
                      <div className="w-3 h-4 rounded-sm" style={{ backgroundColor: p.down }} />
                    </div>
                    <span className="text-xs">{p.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Background selector */}
          <div className="relative" ref={bgMenuRef}>
            {(() => {
              const bgPreset = BACKGROUNDS[chartBg]
              const previewBg = bgPreset?.bg ?? (isDark ? '#111827' : '#ffffff')
              return (
                <button
                  onClick={() => setShowBgMenu(!showBgMenu)}
                  title="Fondo del gráfico"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-750"
                >
                  <div className="w-4 h-3 rounded-sm border border-slate-200 dark:border-gray-600" style={{ backgroundColor: previewBg }} />
                  <Layers size={11} className="text-slate-400" />
                  <ChevronDown size={11} className="text-slate-400" />
                </button>
              )
            })()}
            {showBgMenu && (
              <div className="absolute top-full left-0 mt-1 w-52 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl shadow-xl z-50 p-3">
                <p className="text-[10px] font-semibold text-slate-400 dark:text-gray-500 mb-2 uppercase tracking-wide">Fondo del gráfico</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {Object.entries(BACKGROUNDS).map(([key, b]) => {
                    const previewBg = b.bg ?? (isDark ? '#111827' : '#ffffff')
                    const isSelected = chartBg === key
                    return (
                      <button
                        key={key}
                        onClick={() => { setChartBg(key); setShowBgMenu(false) }}
                        title={b.label}
                        className={`group relative flex flex-col items-center gap-1 p-1 rounded-lg transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-gray-700'}`}
                      >
                        <div
                          className={`w-8 h-6 rounded border-2 transition-all ${isSelected ? 'border-blue-500 shadow-sm shadow-blue-500/40' : 'border-slate-200 dark:border-gray-600'}`}
                          style={key === 'default'
                            ? { background: 'linear-gradient(135deg, #111827 50%, #ffffff 50%)' }
                            : { backgroundColor: previewBg }
                          }
                        />
                        <span className={`text-[8px] leading-tight text-center truncate w-full ${isSelected ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-slate-500 dark:text-gray-400'}`}>
                          {b.label}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Indicators dropdown */}
          <div className="relative" ref={indicatorsMenuRef}>
            <button
              onClick={() => setShowIndicatorsMenu(!showIndicatorsMenu)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                showIndicatorsMenu || activeIndicatorCount > 0
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400'
                  : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-750'
              }`}
            >
              <LineChart size={13} />
              <span className="hidden sm:inline">Indicadores</span>
              {activeIndicatorCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{activeIndicatorCount}</span>
              )}
              <ChevronDown size={11} className={`transition-transform duration-150 ${showIndicatorsMenu ? 'rotate-180' : ''}`} />
            </button>

            {showIndicatorsMenu && (
              <div className="absolute top-full left-0 mt-1 w-64 max-h-[32rem] overflow-y-auto bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl shadow-xl z-50">
                {(() => {
                  const structureIds = ['choch_bos_quantum', 'fvg_ifvg_quantum', 'order_blocks_quantum', 'mitigation_block_quantum', 'liquidity_pools_quantum', 'liquidity', 'canales_sr']
                  const trendIds = ['vwap', 'bollinger_bands_quantum', 'ema_sma_quantum']
                  const strategyIds = ['showict_quantum', 'squeeze_breakout', 'smart_breakout', 'patron_ruptura_quantum', 'nasdaq_quantum']
                  // Legacy retirados del catálogo: bollinger (v10, carpeta
                  // inerte), breakout_pattern (2026-08-06: eliminado del
                  // repo; su motor vive en patron_ruptura_quantum/engine.js,
                  // byte-idéntico, y PRQ es su única presentación) y
                  // showi_ict (2026-08-08: eliminado del repo; su motor vive
                  // en showict_quantum/engine.js, byte-idéntico — v13).
                  const retiredIds = ['bollinger', 'breakout_pattern', 'showi_ict']
                  const structureRegistry = indicators.filter(ind => structureIds.includes(ind.id))
                  const trendRegistry = indicators.filter(ind => trendIds.includes(ind.id))
                  const strategyRegistry = indicators.filter(ind => strategyIds.includes(ind.id))
                  const otherRegistry = indicators.filter(ind => ![...structureIds, ...trendIds, ...strategyIds, ...retiredIds].includes(ind.id))

                  const renderRegistry = (list) => list.map(ind => (
                    <IndicatorToggle
                      key={ind.id}
                      active={!!activeIndicators[ind.id]}
                      onChange={(v) => {
                        setRegistryInd(ind.id, v)
                        if (v === true && getConfig(ind.id)?.enabled === false) {
                          setConfig(ind.id, { enabled: true })
                        }
                        if (v === true && ind.id === 'ema_sma_quantum') {
                          movingAverages.forEach(ma => {
                            if (!ma.active) updateMovingAverage(ma.id, { active: true })
                          })
                        }
                        if (v === false && ind.id === 'ema_sma_quantum') {
                          movingAverages.forEach(ma => {
                            if (ma.active) updateMovingAverage(ma.id, { active: false })
                          })
                        }
                      }}
                      onSettings={ind.PanelComponent ? () => {
                        setOpenPanels(prev => ({ ...prev, [ind.id]: true }))
                        setShowIndicatorsMenu(false)
                      } : undefined}
                      color="#22c55e"
                      label={ind.name}
                    />
                  ))

                  return (
                    <>
                      {/* ── ESTRUCTURA ── */}
                      <div className="px-3 pt-2.5 pb-1">
                        <span className="text-[10px] font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest">Estructura</span>
                      </div>

                      {renderRegistry(structureRegistry)}

                      {/* ── TENDENCIA ── */}
                      <div className="px-3 pt-2.5 pb-1 mt-1 border-t border-slate-100 dark:border-gray-700/60">
                        <span className="text-[10px] font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest">Tendencia</span>
                      </div>
                      {renderRegistry(trendRegistry)}

                      {/* ── ESTRATEGIAS ── */}
                      <div className="px-3 pt-2.5 pb-1 mt-1 border-t border-slate-100 dark:border-gray-700/60">
                        <span className="text-[10px] font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest">Estrategias</span>
                      </div>
                      <IndicatorToggle active={showICT} onChange={setShowICT} color="#22d3ee" label="ICT / SMC" />
                      {strategyRegistry.length === 0 && otherRegistry.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400 dark:text-gray-500">No hay estrategias cargadas</div>
                      )}
                      {renderRegistry(strategyRegistry)}
                      {renderRegistry(otherRegistry)}
                      <div className="pb-1.5" />
                    </>
                  )
                })()}
              </div>
            )}
          </div>

          {/* Selected position badge */}
          {selectedPosition && (
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                selectedPosition.side === 'long'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {selectedPosition.side.toUpperCase()} @ {formatPrice(selectedPosition.entry_price)}
              </span>
              <button onClick={() => setSelectedPosition(null)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-gray-300 rounded-md hover:bg-slate-100 dark:hover:bg-gray-700">
                <X size={13} />
              </button>
            </div>
          )}

          {/* Right-side actions */}
          <div className="ml-auto flex items-center gap-1.5">

            {/* Favorites dropdown */}
            <FavoritesMenu
              favorites={favorites}
              selectedSymbol={selectedSymbol}
              onSelect={(sym) => { setSelectedSymbol(sym); setDataSource(deriveCandleSource(sym)); setShowFavMenu(false) }}
              onToggleFavorite={toggleFavorite}
              showMenu={showFavMenu}
              onToggleMenu={() => setShowFavMenu(v => !v)}
              menuRef={favMenuRef}
            />

            {/* Panel toggle buttons */}
            {[
              { key: 'openPos',   label: 'Pos. open',  badge: openForSymbol.length },
              { key: 'closedPos', label: 'Pos. close', badge: null },
            ].map(({ key, label, badge }) => (
              <div key={key} className="relative hidden md:block">
                <button
                  onClick={() => {
                    setSidebarVisible(v => ({ ...v, [key]: !v[key] }))
                    if (key === 'closedPos' && !closedLoaded) {
                      setClosedLoaded(true)
                      positionsService.list({ status: 'closed', limit: 500 })
                        .then(res => {
                          const closed = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []
                          setPositions(prev => [...prev.filter(p => p.status === 'open'), ...closed])
                        })
                        .catch(() => {})
                    }
                  }}
                  title={label}
                  className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                    sidebarVisible[key]
                      ? 'bg-slate-700 dark:bg-slate-200 border-slate-700 dark:border-slate-200 text-white dark:text-slate-800'
                      : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-750'
                  }`}>
                  {label}
                </button>
                {badge > 0 && !sidebarVisible[key] && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold bg-blue-500 text-white rounded-full pointer-events-none">
                    {badge}
                  </span>
                )}
              </div>
            ))}

            <button
              onClick={() => setDrawingToolbarVisible(v => !v)}
              title="Herramientas de dibujo"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                drawingToolbarVisible
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-750'
              }`}
            >
              <Pencil size={13} />
              <span className="hidden sm:inline">Dibujar</span>
            </button>

            <button
              onClick={() => setShowTradePanel(v => !v)}
              title="Abrir panel de trading"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                showTradePanel
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-750'
              }`}
            >
              <Crosshair size={13} />
              <span className="hidden sm:inline">Trade</span>
            </button>
            <PlaybackControls
              isPlayback={pb.isPlayback}
              isSupported={pb.isSupported}
              isPlaying={pb.isPlaying}
              currentIndex={pb.currentIndex}
              totalBars={pb.totalBars}
              speed={pb.speed}
              onEnter={pb.enterPlayback}
              onExit={pb.exitPlayback}
              onPlay={pb.play}
              onPause={pb.pause}
              onStepBack={pb.stepBackward}
              onStepForward={pb.stepForward}
              onSeek={pb.seek}
              onSpeedChange={pb.setSpeed}
            />

            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-750 text-slate-500 dark:text-gray-400"
            >
              {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
        </div>

        {/* ── Active indicator chips ─────────────────────────────────────────── */}
        {activeIndicatorCount > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 shrink-0 px-0.5">
            {movingAverages.filter(ma => ma.active).map(ma => (
              <span
                key={ma.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300"
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ma.color }} /> {ma.type.toUpperCase()} {ma.period}
                <button onClick={() => toggleMovingAverage(ma.id, false)} className="hover:text-slate-900 dark:hover:text-gray-100"><X size={10} /></button>
              </span>
            ))}
            {showRsi && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                <span className="w-2 h-2 rounded-full bg-[#a855f7]" /> RSI 14
                <button onClick={() => setShowRsi(false)} className="hover:text-purple-900"><X size={10} /></button>
              </span>
            )}
            {showVolume && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300">
                <span className="w-2 h-2 rounded-full bg-[#64748b]" /> Volumen
                <button onClick={() => setShowVolume(false)} className="hover:text-slate-900"><X size={10} /></button>
              </span>
            )}
            {showICT && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400">
                <span className="w-2 h-2 rounded-full bg-[#22d3ee]" /> ICT / SMC
                <button onClick={() => setShowICT(false)} className="hover:text-cyan-900"><X size={10} /></button>
              </span>
            )}
            {indicators.filter(ind => activeIndicators[ind.id]).map(ind => (
              <span key={ind.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <span className="w-2 h-2 rounded-full bg-[#22c55e]" /> {ind.name}
                <button onClick={() => setRegistryInd(ind.id, false)} className="hover:text-green-900"><X size={10} /></button>
              </span>
            ))}
          </div>
        )}

        {/* ── Main area ──────────────────────────────────────────────────────── */}
        <div className="flex-1 flex gap-2 min-h-0 overflow-hidden">

          {/* Chart */}
          <div className="flex-1 min-w-0 rounded-xl overflow-hidden relative border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <ChartErrorBoundary key={chartRemountKey} onRetry={() => setChartRemountKey(k => k + 1)}>
            <ChartRenderer
              key={chartType}
              data={candleData}
              dataRef={dataRef}
              subscribe={subscribe}
              dataVersion={tick}
              chartType={chartType}
              palette={palette}
              chartBg={chartBg}
              isDark={isDark}
              tradingMode={tradingMode}
              symbol={selectedSymbol}
              timeframe={timeframe}
              candleColorsRef={candleColorsRef}
              colorVersion={colorVersion}
              disableRealtimeUpdates={pb.isPlayback}
              onChartReady={(chart, series) => {
                chartRef.current = chart
                candleSeriesRef.current = series
                setChartVersion(v => v + 1)
              }}
              onMouseDown={handleChartMouseDown}
              onMouseMove={handleChartMouseMove}
              onMouseUp={handleChartMouseUp}
              onMouseLeave={handleChartMouseUp}
            />

            {/* ── Drawing tools overlay ─────────────────────────────────────── */}
            {!loading && candleData.length > 0 && (
              <DrawingTools
                chartRef={chartRef}
                candleSeriesRef={candleSeriesRef}
                isDark={isDark}
                symbol={selectedSymbol}
                chartVersion={chartVersion}
                visible={drawingToolbarVisible}
                onClose={() => setDrawingToolbarVisible(false)}
              />
            )}

            {/* ── ICT/SMC canvas overlay ────────────────────────────────────── */}
            {showICT && ictData && (
              <ICTCanvas
                chartRef={chartRef}
                seriesRef={candleSeriesRef}
                data={ictData}
                config={ictConfig}
                signal={ictSignal}
                historySignals={ictHistoryData}
                chartVersion={chartVersion}
                chartBackground={chartBackground}
              />
            )}
            {showICT && showICTConfig && (
              <ICTConfigPanel
                config={ictConfig}
                onChange={setIctConfig}
                onClose={() => setShowICTConfig(false)}
                loading={ictMainLoading || ictHistoryLoading}
              />
            )}

            {isLoading && (
              <div className="absolute top-2 right-2 flex items-center gap-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-gray-700 shadow-sm">
                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-slate-500 dark:text-gray-400">Cargando…</span>
              </div>
            )}
            {tradingMode && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-amber-50/95 dark:bg-amber-900/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-amber-300 dark:border-amber-600 shadow-sm pointer-events-none">
                <Crosshair size={12} className="text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Haz clic en el precio de entrada</span>
              </div>
            )}
            {error && !isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50/90 dark:bg-gray-950/90">
                <div className="text-center p-6">
                  <AlertTriangle size={28} className="text-amber-500 mx-auto mb-2" />
                  <p className="text-slate-700 dark:text-gray-300 font-medium text-sm mb-1">No se pudieron cargar los datos</p>
                  <p className="text-slate-400 dark:text-gray-500 text-xs max-w-xs">{error}</p>
                </div>
              </div>
            )}

            {/* ── HTML signal labels (A+/A/A-) ──────────────────────────────── */}
            <IndicatorOverlayLabels labels={overlayLabels} />
            </ChartErrorBoundary>

            {/* Indicator Badges */}
            {showICT && (
              <div
                className="absolute top-0 left-0 z-10"
                style={{ transform: `translateY(8px)` }}
              >
                <ICTBadge
                  onOpenSettings={() => setShowICTConfig(true)}
                  onClose={() => setShowICT(false)}
                />
              </div>
            )}
            {(() => {
              const active = indicators.filter(ind => activeIndicators[ind.id])
              const baseOffset = (showICT ? 52 : 8)
              return active.map((ind, idx) => {
                const Badge = ind.BadgeComponent
                if (!Badge) return null
                const extraProps = ind.id === 'ema_sma_quantum' ? {
                  addMovingAverage, removeMovingAverage, toggleMovingAverage, updateMovingAverage,
                  config: getConfig(ind.id),
                } : ind.id === 'rango_quantum' ? {
                  config: getConfig(ind.id, chartCellKey),
                  botDivergence: rangoBotDivergence,
                } : undefined
                return (
                  <div
                    key={`badge-${ind.id}`}
                    className="absolute top-0 left-0 z-10"
                    style={{ transform: `translateY(${baseOffset + idx * 44}px)` }}
                  >
                    <Badge
                      result={indResults[ind.id]}
                      version={ind.version}
                      onOpenSettings={() => setOpenPanels(prev => ({ ...prev, [ind.id]: true }))}
                      onClose={() => setRegistryInd(ind.id, false)}
                      {...extraProps}
                    />
                  </div>
                )
              })
            })()}

            {/* Indicator Settings Panels */}
            {(() => {
              const active = indicators.filter(ind => activeIndicators[ind.id] && ind.PanelComponent && openPanels[ind.id])
              return active.map((ind) => {
                const Panel = ind.PanelComponent
                // ema_sma_quantum: las mutaciones de medias (añadir/editar/
                // quitar, incluido estilo/grosor) van por las acciones REALES
                // de useIndicatorState (movingAverages, fuente de verdad),
                // igual que ya hace el badge — nunca por onChange('mas', ...)
                // directo sobre trading_bot_indicator_configs, que useEmaSmaSync
                // pisaría en el siguiente ciclo con el movingAverages sin
                // sincronizar (bug: la edición del panel se revertía sola).
                const panelExtraProps = ind.id === 'ema_sma_quantum' ? {
                  addMovingAverage, removeMovingAverage, toggleMovingAverage, updateMovingAverage,
                } : undefined
                return (
                  <Panel
                    key={`panel-${ind.id}`}
                    // El panel edita la CELDA símbolo×TF activa (override
                    // parcial; la global queda como fallback universal).
                    config={getConfig(ind.id, chartCellKey)}
                    onChange={(next) => setConfig(ind.id, next, chartCellKey)}
                    onClose={() => setOpenPanels(prev => ({ ...prev, [ind.id]: false }))}
                    chartTimeframe={timeframe}
                    chartSymbol={selectedSymbol}
                    {...panelExtraProps}
                  />
                )
              })
            })()}

            <EmaSmaNotificationToast event={emaSmaToast} onDismiss={dismissEmaSmaToast} />
            <EmaSmaNotificationToast event={emaSmaToggleToast} onDismiss={dismissEmaSmaToggleToast} stacked={!!emaSmaToast} testId="ema-sma-toggle-toast" />
          </div>

          {/* ── Sidebar ──────────────────────────────────────────────────────── */}
          {(showTradePanel || Object.values(sidebarVisible).some(Boolean)) && (
          <div className="w-72 flex-shrink-0 flex flex-col gap-2 overflow-y-auto">

            {/* Trade panel */}
            {showTradePanel && (
              <div className="card py-3 px-3 space-y-2.5 flex-shrink-0 border-blue-200 dark:border-blue-800/50">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold flex items-center gap-1.5 text-slate-700 dark:text-gray-300">
                    <Crosshair size={12} className="text-blue-500" />
                    Orden rápida · {selectedSymbol.replace('/USDT:USDT', '').replace('/USDC:USDC', '')}
                  </h3>
                  <button onClick={() => { setShowTradePanel(false); setTradingMode(false); setTradeEntryPrice(null); setTradeFeedback(null) }}
                    className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-gray-300">
                    <X size={12} />
                  </button>
                </div>

                {/* Account */}
                {tradeAccounts.length > 0 ? (
                  <select
                    value={tradeAccountId}
                    onChange={e => {
                      const acc = tradeAccounts.find(a => a.id === e.target.value)
                      setTradeAccountId(e.target.value)
                      setTradeAccountType(acc?.type || 'real')
                    }}
                    className="w-full text-xs bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-slate-700 dark:text-gray-300"
                  >
                    {tradeAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.label} ({a.exchange})</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-[11px] text-slate-400 dark:text-gray-600">Cargando cuentas…</p>
                )}

                {/* Order type: Market / Limit */}
                <div className="space-y-1.5">
                  <span className="text-[10px] font-medium text-slate-400 dark:text-gray-500 uppercase tracking-wide">Tipo de orden</span>
                  <div className="grid grid-cols-2 gap-1">
                    <button
                      onClick={() => { setTradeEntryPrice(null); setTradingMode(false) }}
                      className={`text-[11px] font-semibold py-1.5 rounded-lg border transition-colors ${
                        !tradeEntryPrice && !tradingMode
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'bg-slate-50 dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-500 dark:text-gray-400 hover:border-blue-300 dark:hover:border-blue-600'
                      }`}
                    >
                      Market
                    </button>
                    <button
                      onClick={() => { setTradingMode(true); setTradeEntryPrice(null) }}
                      className={`text-[11px] font-semibold py-1.5 rounded-lg border transition-colors flex items-center justify-center gap-1 ${
                        tradingMode
                          ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-400'
                          : tradeEntryPrice
                            ? 'bg-amber-500 border-amber-500 text-white'
                            : 'bg-slate-50 dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-500 dark:text-gray-400 hover:border-amber-300 dark:hover:border-amber-600'
                      }`}
                    >
                      <Crosshair size={10} />
                      Limit
                    </button>
                  </div>
                  {tradingMode && (
                    <p className="text-[10px] text-center text-amber-600 dark:text-amber-400">Haz clic en el gráfico para fijar precio</p>
                  )}
                  {tradeEntryPrice && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <Crosshair size={10} className="text-amber-500 flex-shrink-0" />
                      <span className="text-[11px] font-mono text-amber-700 dark:text-amber-400 flex-1 font-semibold">${formatPrice(tradeEntryPrice)}</span>
                      <button onClick={() => { setTradeEntryPrice(null); setTradingMode(true) }} title="Cambiar precio"
                        className="p-0.5 text-amber-400 hover:text-amber-600 dark:hover:text-amber-300 rounded">
                        <Crosshair size={9} />
                      </button>
                      <button onClick={() => { setTradeEntryPrice(null); setTradingMode(false) }} title="Volver a market"
                        className="p-0.5 text-slate-400 hover:text-red-500 dark:hover:text-red-400 rounded">
                        <X size={9} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Leverage + Size */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <span className="text-[10px] font-medium text-slate-400 dark:text-gray-500 uppercase tracking-wide">Leverage</span>
                    <div className="flex items-center gap-1">
                      <input type="number" value={tradeLeverage} onChange={e => setTradeLeverage(Math.max(1, Math.min(125, Number(e.target.value))))}
                        min={1} max={125}
                        className="flex-1 text-xs text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-1.5 py-1.5 text-slate-700 dark:text-gray-300 w-0" />
                      <span className="text-[11px] text-slate-400">x</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] font-medium text-slate-400 dark:text-gray-500 uppercase tracking-wide">Tamaño</span>
                    <div className="flex items-center gap-1">
                      <input type="number" value={tradeSizingValue} onChange={e => setTradeSizingValue(Number(e.target.value))}
                        min={0.1} step={tradeSizingType === 'percentage' ? 1 : 10}
                        className="flex-1 text-xs text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-1.5 py-1.5 text-slate-700 dark:text-gray-300 w-0" />
                      <button onClick={() => setTradeSizingType(t => t === 'percentage' ? 'fixed' : 'percentage')}
                        className="text-[11px] px-1.5 py-1 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-md text-slate-600 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700">
                        {tradeSizingType === 'percentage' ? '%' : 'U'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* SL */}
                <div className="space-y-1">
                  <span className="text-[10px] font-medium text-slate-400 dark:text-gray-500 uppercase tracking-wide">Stop Loss</span>
                  <div className="flex items-center gap-1.5">
                    <input type="number" value={tradeSlPct} onChange={e => setTradeSlPct(Number(e.target.value))}
                      min={0.1} step={0.1}
                      className="flex-1 text-xs text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-slate-700 dark:text-gray-300" />
                    <span className="text-[11px] text-slate-400 dark:text-gray-500">% desde entrada</span>
                  </div>
                </div>

                {/* Estrategia (collapsible) */}
                <div className="border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <button onClick={() => setTradeAdvOpen(v => !v)}
                    className="w-full flex items-center justify-between px-2.5 py-1.5 bg-slate-50 dark:bg-gray-800/60 hover:bg-slate-100 dark:hover:bg-gray-700/60">
                    <span className="text-[10px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1">
                      {tradeAdvOpen ? <Minus size={10} /> : <Plus size={10} />}
                      Estrategia
                    </span>
                    {(tradeTPs.length > 0 || tradeTrailing.enabled || tradeBreakeven.enabled || tradeDynamicSL.enabled) && (
                      <span className="text-[9px] font-bold bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded-full">
                        {[tradeTPs.length > 0 && `${tradeTPs.length}TP`, tradeTrailing.enabled && 'Trail', tradeBreakeven.enabled && 'BE', tradeDynamicSL.enabled && 'DSL'].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </button>

                  {tradeAdvOpen && (
                    <div className="px-2.5 py-2 space-y-3">
                      {/* Take Profits */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Take Profits</span>
                          <button onClick={() => setTradeTPs(tps => [...tps, { profit_percent: 2, close_percent: 50 }])}
                            className="text-[10px] text-indigo-500 hover:text-indigo-400 font-semibold">+ Añadir</button>
                        </div>
                        {tradeTPs.length === 0
                          ? <p className="text-[10px] text-slate-400 dark:text-gray-600 italic">Sin TPs</p>
                          : tradeTPs.map((tp, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <div className="flex-1 flex items-center gap-1">
                                <input type="number" value={tp.profit_percent} min={0.1} step={0.1}
                                  onChange={e => setTradeTPs(tps => tps.map((t, j) => j === i ? { ...t, profit_percent: Number(e.target.value) } : t))}
                                  className="w-0 flex-1 text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                                <span className="text-[10px] text-slate-400 flex-shrink-0">%</span>
                              </div>
                              <div className="flex-1 flex items-center gap-1">
                                <input type="number" value={tp.close_percent} min={1} max={100} step={5}
                                  onChange={e => setTradeTPs(tps => tps.map((t, j) => j === i ? { ...t, close_percent: Number(e.target.value) } : t))}
                                  className="w-0 flex-1 text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                                <span className="text-[10px] text-slate-400 flex-shrink-0">cierre</span>
                              </div>
                              <button onClick={() => setTradeTPs(tps => tps.filter((_, j) => j !== i))}
                                className="p-0.5 text-slate-400 hover:text-red-500 flex-shrink-0"><X size={10} /></button>
                            </div>
                          ))
                        }
                        {tradeTPs.length > 0 && <p className="text-[9px] text-slate-400 dark:text-gray-600">Ganancia % · % posición a cerrar</p>}
                      </div>
                      {/* Trailing Stop */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={tradeTrailing.enabled} onChange={e => setTradeTrailing(t => ({ ...t, enabled: e.target.checked }))} className="accent-indigo-500" />
                          <span className="text-[10px] font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wide">Trailing Stop</span>
                        </label>
                        {tradeTrailing.enabled && (
                          <div className="grid grid-cols-2 gap-1.5 pl-4">
                            <div className="space-y-0.5">
                              <span className="text-[9px] text-slate-400">Activación %</span>
                              <input type="number" value={tradeTrailing.activation_profit} min={0.1} step={0.1}
                                onChange={e => setTradeTrailing(t => ({ ...t, activation_profit: Number(e.target.value) }))}
                                className="w-full text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-[9px] text-slate-400">Callback %</span>
                              <input type="number" value={tradeTrailing.callback_rate} min={0.1} step={0.1}
                                onChange={e => setTradeTrailing(t => ({ ...t, callback_rate: Number(e.target.value) }))}
                                className="w-full text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Breakeven */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={tradeBreakeven.enabled} onChange={e => setTradeBreakeven(t => ({ ...t, enabled: e.target.checked }))} className="accent-indigo-500" />
                          <span className="text-[10px] font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wide">Breakeven</span>
                        </label>
                        {tradeBreakeven.enabled && (
                          <div className="grid grid-cols-2 gap-1.5 pl-4">
                            <div className="space-y-0.5">
                              <span className="text-[9px] text-slate-400">Activación %</span>
                              <input type="number" value={tradeBreakeven.activation_profit} min={0.1} step={0.1}
                                onChange={e => setTradeBreakeven(t => ({ ...t, activation_profit: Number(e.target.value) }))}
                                className="w-full text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-[9px] text-slate-400">Lock %</span>
                              <input type="number" value={tradeBreakeven.lock_profit} min={0} step={0.1}
                                onChange={e => setTradeBreakeven(t => ({ ...t, lock_profit: Number(e.target.value) }))}
                                className="w-full text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Dynamic SL */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={tradeDynamicSL.enabled} onChange={e => setTradeDynamicSL(t => ({ ...t, enabled: e.target.checked }))} className="accent-indigo-500" />
                          <span className="text-[10px] font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wide">SL Dinámico</span>
                        </label>
                        {tradeDynamicSL.enabled && (
                          <div className="grid grid-cols-2 gap-1.5 pl-4">
                            <div className="space-y-0.5">
                              <span className="text-[9px] text-slate-400">Paso %</span>
                              <input type="number" value={tradeDynamicSL.step_percent} min={0.1} step={0.1}
                                onChange={e => setTradeDynamicSL(t => ({ ...t, step_percent: Number(e.target.value) }))}
                                className="w-full text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-[9px] text-slate-400">Máx pasos</span>
                              <input type="number" value={tradeDynamicSL.max_steps} min={1} step={1}
                                onChange={e => setTradeDynamicSL(t => ({ ...t, max_steps: Number(e.target.value) }))}
                                className="w-full text-[11px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-1 text-slate-700 dark:text-gray-300" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Feedback */}
                {tradeFeedback && (
                  <div className={`text-[11px] px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 ${
                    tradeFeedback.type === 'ok'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                  }`}>
                    {tradeFeedback.msg}
                  </div>
                )}

                {/* LONG / SHORT */}
                <div className="grid grid-cols-2 gap-1.5">
                  <button onClick={() => executeTrade('long')}
                    disabled={!tradeAccountId || tradeLoading}
                    className="py-2 px-3 text-xs font-bold bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white rounded-lg flex items-center justify-center gap-1.5 transition-colors">
                    {tradeLoading ? <Loader2 size={11} className="animate-spin" /> : <TrendingUp size={11} />}
                    LONG
                  </button>
                  <button onClick={() => executeTrade('short')}
                    disabled={!tradeAccountId || tradeLoading}
                    className="py-2 px-3 text-xs font-bold bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg flex items-center justify-center gap-1.5 transition-colors">
                    {tradeLoading ? <Loader2 size={11} className="animate-spin" /> : <TrendingDown size={11} />}
                    SHORT
                  </button>
                </div>
              </div>
            )}

            {/* Strategy config — duplicate removed, now lives inside trade panel */}
            {/* Open positions */}
            {sidebarVisible.openPos && (
            <div className="card py-3 px-3 space-y-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold flex items-center gap-1.5 text-slate-700 dark:text-gray-300">
                  <Activity size={12} className="text-blue-500" />
                  Posiciones abiertas
                </h3>
                <span className="text-[10px] font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full">{openForSymbol.length}</span>
              </div>
              {openForSymbol.length === 0
                ? <p className="text-xs text-slate-400 dark:text-gray-600">Sin posiciones abiertas</p>
                : openForSymbol.map(pos => (
                    <div key={pos.id}
                      onClick={() => setSelectedPosition(selectedPosition?.id === pos.id ? null : pos)}
                      className={`p-2.5 rounded-lg text-xs cursor-pointer transition-all ${
                        selectedPosition?.id === pos.id
                          ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'bg-slate-50 dark:bg-gray-800/60 hover:bg-slate-100 dark:hover:bg-gray-700'
                      }`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`font-bold ${pos.side === 'long' ? 'text-green-500' : 'text-red-500'}`}>
                          {pos.side === 'long' ? 'LONG' : 'SHORT'}
                        </span>
                        <span className="font-mono text-slate-600 dark:text-gray-400">{formatPrice(pos.entry_price)}</span>
                      </div>
                      {(() => {
                        const roi = calculatePositionRoi(pos, prices[pos.symbol])
                        return (
                          <div className={`font-mono font-semibold mb-1.5 ${roi >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {roi >= 0 ? '+' : ''}{roi.toFixed(2)}% ROI (x{pos.leverage || 1})
                          </div>
                        )
                      })()}
                      <div className="space-y-0.5 text-[11px] text-slate-500 dark:text-gray-500">
                        <div className="flex justify-between"><span>Qty:</span><span className="font-mono">{parseFloat(pos.quantity).toFixed(4)}</span></div>
                        <div className="flex justify-between"><span>SL:</span><span className="font-mono text-red-400">{formatPrice(pos.current_sl_price) || '—'}</span></div>
                        {pos.current_tp_prices?.length > 0 && (
                          <div className="flex justify-between"><span>TP:</span><span className="font-mono text-green-400">{pos.current_tp_prices.map(tp => formatPrice(tp?.price ?? tp)).join(', ')}</span></div>
                        )}
                      </div>
                      {/* Strategy edit panel — only when position is selected */}
                      {selectedPosition?.id === pos.id && (
                        <div className="mt-2 border-t border-slate-200 dark:border-gray-700 pt-2" onClick={e => e.stopPropagation()}>
                          <button onClick={() => setShowStrategyPanel(v => !v)}
                            className="flex items-center justify-between w-full text-[10px] font-semibold text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200">
                            <span>Modificar estrategia</span>
                            <span>{showStrategyPanel ? '▲' : '▼'}</span>
                          </button>
                          {showStrategyPanel && (
                            <div className="mt-2 space-y-2">
                              {/* TPs */}
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400 dark:text-gray-500">Take Profits</span>
                                  <button onClick={() => setStrategyEdit(s => ({ ...s, tps: [...s.tps, { profit_percent: 2, close_percent: 50 }] }))}
                                    className="text-[9px] text-blue-500 hover:text-blue-700 font-semibold">+ TP</button>
                                </div>
                                {strategyEdit.tps.length === 0
                                  ? <p className="text-[9px] text-slate-400 italic">Sin TPs</p>
                                  : strategyEdit.tps.map((tp, i) => (
                                    <div key={i} className="flex items-center gap-1">
                                      <input type="number" value={tp.profit_percent} min={0.1} step={0.1}
                                        onChange={e => setStrategyEdit(s => ({ ...s, tps: s.tps.map((t, j) => j === i ? { ...t, profit_percent: Number(e.target.value) } : t) }))}
                                        className="w-14 text-[10px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-0.5 text-slate-700 dark:text-gray-300" />
                                      <span className="text-[9px] text-slate-400">%</span>
                                      <input type="number" value={tp.close_percent} min={1} max={100} step={5}
                                        onChange={e => setStrategyEdit(s => ({ ...s, tps: s.tps.map((t, j) => j === i ? { ...t, close_percent: Number(e.target.value) } : t) }))}
                                        className="w-14 text-[10px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-0.5 text-slate-700 dark:text-gray-300" />
                                      <span className="text-[9px] text-slate-400">cierre%</span>
                                      <button onClick={() => setStrategyEdit(s => ({ ...s, tps: s.tps.filter((_, j) => j !== i) }))}
                                        className="text-red-400 hover:text-red-600 text-[10px] ml-auto">×</button>
                                    </div>
                                  ))
                                }
                              </div>
                              {/* Trailing */}
                              <div className="space-y-1">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input type="checkbox" checked={strategyEdit.trailing.enabled}
                                    onChange={e => setStrategyEdit(s => ({ ...s, trailing: { ...s.trailing, enabled: e.target.checked } }))}
                                    className="accent-blue-500 w-3 h-3" />
                                  <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400 dark:text-gray-500">Trailing Stop</span>
                                </label>
                                {strategyEdit.trailing.enabled && (
                                  <div className="flex gap-1 pl-4">
                                    <div className="flex-1 space-y-0.5">
                                      <span className="text-[8px] text-slate-400">Activar en %</span>
                                      <input type="number" value={strategyEdit.trailing.activation_profit} min={0.1} step={0.1}
                                        onChange={e => setStrategyEdit(s => ({ ...s, trailing: { ...s.trailing, activation_profit: Number(e.target.value) } }))}
                                        className="w-full text-[10px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-0.5 text-slate-700 dark:text-gray-300" />
                                    </div>
                                    <div className="flex-1 space-y-0.5">
                                      <span className="text-[8px] text-slate-400">Retroceso %</span>
                                      <input type="number" value={strategyEdit.trailing.callback_rate} min={0.1} step={0.1}
                                        onChange={e => setStrategyEdit(s => ({ ...s, trailing: { ...s.trailing, callback_rate: Number(e.target.value) } }))}
                                        className="w-full text-[10px] text-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1 py-0.5 text-slate-700 dark:text-gray-300" />
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => applyStrategyChanges(pos)} disabled={strategyLoading}
                                className="w-full py-1 text-[10px] font-semibold rounded-md bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white transition-colors">
                                {strategyLoading ? 'Aplicando…' : 'Aplicar cambios'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); closePosition(pos) }}
                        className="mt-2 w-full py-1 text-[11px] font-semibold rounded-md bg-slate-200 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 text-slate-600 dark:text-gray-400 transition-colors">
                        Cerrar posición
                      </button>
                    </div>
                  ))
              }
            </div>
            )}

            {/* Closed positions */}
            {sidebarVisible.closedPos && (
            <div className="card py-3 px-3 space-y-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold flex items-center gap-1.5 text-slate-700 dark:text-gray-300">
                  <TrendingUp size={12} className="text-purple-500" />
                  Posiciones cerradas
                </h3>
                <span className="text-[10px] font-bold bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full">{closedForSymbol.length}</span>
              </div>
              {closedForSymbol.length === 0
                ? <p className="text-xs text-slate-400 dark:text-gray-600">Sin posiciones cerradas</p>
                : (
                  <div className="space-y-1.5 max-h-52 overflow-y-auto pr-0.5">
                    {closedForSymbol.map(pos => {
                      const pnl = parseFloat(pos.realized_pnl || 0)
                      return (
                        <div key={pos.id}
                          onClick={() => setSelectedPosition(selectedPosition?.id === pos.id ? null : pos)}
                          className={`p-2.5 rounded-lg text-xs cursor-pointer transition-all ${
                            selectedPosition?.id === pos.id
                              ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'bg-slate-50 dark:bg-gray-800/60 hover:bg-slate-100 dark:hover:bg-gray-700'
                          }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-bold ${pos.side === 'long' ? 'text-green-500' : 'text-red-500'}`}>
                              {pos.side === 'long' ? 'LONG' : 'SHORT'}
                            </span>
                            <span className={`font-mono font-bold ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} USDT
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-400 dark:text-gray-600">
                            {formatTime(pos.opened_at)} → {formatTime(pos.closed_at)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              }
            </div>
            )}

          </div>)}{/* /sidebar */}
        </div>{/* /main */}
      </div>{/* /inner */}
    </div>
  )
}
